import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import cytoscape from 'cytoscape';
import fcose from 'cytoscape-fcose';
import radialLayout from './radialLayout';
import gridLayout from './gridLayout';
import { useTheme } from '@mui/material/styles';
import { makeSearchPredicate } from './searchUtils';
import { HOST_FILTER_TESTS } from './LayerToggles';

// Registra o layout fcose uma vez só
if (!cytoscape.__fcoseRegistered) {
  cytoscape.use(fcose);
  cytoscape.__fcoseRegistered = true;
}

const VENDOR_COLOR = {
  HUAWEI: '#c0392b',
  MIKROTIK: '#2980b9',
  VSOL: '#27ae60',
  JUNIPER: '#8e44ad',
  CISCO: '#16a085',
  DATACOM: '#2c3e50',
  A10: '#d35400',
  FIBERHOME: '#f39c12',
  NOKIA: '#7f8c8d',
  UBIQUITI: '#1abc9c',
  ZTE: '#9b59b6',
  PARKS: '#e67e22',
  FURUKAWA: '#bdc3c7',
  CIANET: '#34495e',
  CHIMA: '#95a5a6',
  ZYXEL: '#dfe6e9',
};


function formatBps(bps) {
  if (bps === null || bps === undefined) return null;
  const abs = Math.abs(bps);
  if (abs >= 1e9) return (bps / 1e9).toFixed(2) + ' Gbps';
  if (abs >= 1e6) return (bps / 1e6).toFixed(2) + ' Mbps';
  if (abs >= 1e3) return (bps / 1e3).toFixed(1) + ' Kbps';
  return Math.round(bps) + ' bps';
}

/**
 * Abrevia nome de iface pra caber na label da edge:
 *   GigabitEthernet0/1/2   → Gi0/1/2
 *   TenGigabitEthernet1/0  → Te1/0
 *   XGigabitEthernet0/0/5  → XGi0/0/5
 *   Vlanif100              → Vl100
 *   Eth-Trunk5             → Eth-Trunk5 (não abrevia — operador pediu clareza)
 *   Port-channel1          → Po1
 *   ten-gigabit-ethernet-1/1/1 → Te1/1/1
 *   ethernet 1/1           → Eth 1/1
 *   ether1                 → ether1 (mikrotik, mantém)
 */
/**
 * Rótulo da ponta quando não há interface.
 *
 * O "?" era o problema: ele lê como "não sabemos / está quebrado", quando na
 * verdade são três situações em que interface NÃO SE APLICA, e o backend agora
 * diz qual (`endpoints.motivo_sem_iface`). Medido na TCHE: 170 sessões BGP,
 * 103 rotas e 92 adjacências por descrição — 365 pontas rotuladas como falha
 * sem nenhuma falha existir.
 *
 *   sessão BGP  a sessão é entre IPs, não entre portas. Mostra o AS.
 *   rota        a rota diz "mande pro next-hop"; ela não escolhe porta.
 *   descrição   a adjacência foi inferida do texto de UM lado — o outro nunca
 *               declarou nada, e é por isso que foi inferida.
 *
 * Só quando nada disso explica é que sobra o "?", e aí ele significa de fato
 * "não resolvemos".
 */
function ifaceOrFallback(ifaceName, edgeRaw, side) {
  if (ifaceName) return abbreviateIface(ifaceName);
  const bgp = edgeRaw?.bgp;
  const kinds = edgeRaw?.kinds || [];
  if (bgp && kinds.includes('bgp')) {
    const remote = bgp.peer_as;
    const local = bgp.self_as;
    if (side === 'a' && local) return `AS${local}`;
    if (side === 'b' && remote) return `AS${remote}`;
    if (remote) return `AS${remote}`;
    if (local) return `AS${local}`;
  }
  const motivo = edgeRaw?.endpoints?.motivo_sem_iface;
  if (motivo === 'rota') return 'via rota';
  if (motivo === 'descrição') return 'por descrição';
  if (motivo === 'sessão BGP') return 'sessão BGP';
  return '?';
}

/**
 * Hash determinístico string → cor HSL. Usado pra dar cor independente por
 * subnet shared_lan (cada broadcast domain fica com uma cor) + por bcast node
 * (bordas e ligação). Mesmo input sempre retorna mesma cor.
 */
function colorForString(s) {
  if (!s) return '#64748b';
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) & 0x7fffffff;
  }
  const hue = hash % 360;
  return `hsl(${hue}, 65%, 55%)`;
}

function abbreviateIface(name) {
  if (!name) return '?';
  const n = name.trim();
  // Replace known prefixes
  const patterns = [
    [/^GigabitEthernet/i, 'Gi'],
    [/^TenGigabitEthernet/i, 'Te'],
    [/^XGigabitEthernet/i, 'XGi'],
    [/^100GE/i, '100GE'],
    [/^40GE/i, '40GE'],
    [/^FastEthernet/i, 'Fa'],
    [/^Ethernet(?=\d)/i, 'Eth'],
    [/^Vlanif/i, 'Vl'],
    [/^Vlan-interface/i, 'Vl'],
    [/^interface\s+vlan\s*/i, 'Vl'],
    // Eth-Trunk NÃO abrevia — mantém "Eth-Trunk5" inteiro pra ficar óbvio
    // que é um bundle LAG Huawei. Operador achou "ET5" confuso.
    [/^Port-channel/i, 'Po'],
    [/^Bundle-Ether/i, 'BE'],
    [/^ten-gigabit-ethernet[-\s]/i, 'Te'],
    [/^gigabit-ethernet[-\s]/i, 'Gi'],
    [/^hundred-gigabit-ethernet[-\s]/i, '100Gi'],
    [/^LoopBack/i, 'Lo'],
    [/^Loopback/i, 'Lo'],
  ];
  for (const [rx, repl] of patterns) {
    if (rx.test(n)) return n.replace(rx, repl);
  }
  // Mikrotik / genéricos — mantém
  return n.length > 12 ? n.slice(0, 12) + '…' : n;
}


/**
 * Label do MEIO da edge, mode-aware.
 *
 * Regra (operador):
 *   - L1 alone: NADA no meio. Endpoints cobrem iface. Zero /30, zero VLAN.
 *   - L2 alone: VLAN list só se ≤3 VLANs (senão "10 VLANs" resumo).
 *   - L3 alone: BGP session (/30 vai pro endpoint label, não meio).
 *   - "all": BGP session + VLAN list se ≤3.
 *
 * /30 NUNCA aparece no meio — sempre nos endpoint labels junto do host,
 * pra operador saber que aquele link pode ser gerência (/30 é p2p típico).
 *
 * LAG badge e tráfego aparecem sempre que relevantes.
 */
function buildEdgeLabel(edgeRaw, trafficEntry, matchedVlans, modeSet) {
  const isAll = !modeSet || !(modeSet instanceof Set) || modeSet.has('all') || modeSet.size === 0;
  const parts = [];

  // LAG badge — sempre útil
  const lag = edgeRaw?.lag;
  if (lag) {
    const ma = lag.a?.members?.length || 0;
    const mb = lag.b?.members?.length || 0;
    const total = Math.max(ma, mb);
    const mode = lag.a?.mode || lag.b?.mode || 'static';
    if (total > 0) parts.push(`⧉ ${total}×LAG (${mode})`);
  }

  // Sub-interface em edges unfolded (L1 explodido): mostra iface_a ↔ iface_b
  // no meio pra operador identificar qual Vlanif/sub carrega aquela linha.
  // Só aplica pra children do unfold (is_unfold_child=true) porque nas edges
  // foldadas originais isso seria redundante com a label de endpoint.
  if (edgeRaw?.is_unfold_child || edgeRaw?.parent_edge_id) {
    const epA = edgeRaw?.endpoints?.a || {};
    const epB = edgeRaw?.endpoints?.b || {};
    const ifaceA = epA.iface || '?';
    const ifaceB = epB.iface || '?';
    if (ifaceA !== '?' || ifaceB !== '?') {
      parts.push(`${ifaceA} ↔ ${ifaceB}`);
    }
  }

  // /30 (subnet) no meio da linha SEMPRE que existir. Operador pediu:
  // facilita identificar p2p de gerência/uplink em qualquer mode.
  if (edgeRaw?.subnet) parts.push(edgeRaw.subnet);

  // Label do meio sempre traz todo contexto disponível — operador vai querer
  // ver L1 (/30, sub-iface) + L2 (VLAN) + L3 (BGP/OSPF/MPLS) ao mesmo tempo
  // em qualquer mode. Filtro de mode mexe só em VISIBILIDADE de edges, não
  // em conteúdo de label.
  const showL2 = true;
  const showL3 = true;

  if (showL2) {
    if (matchedVlans && matchedVlans.length > 0) {
      // VLANs highlighted (VLAN path mode) — sempre mostra, é seleção explícita
      parts.push('▮ VLAN ' + matchedVlans.join(' ▮ '));
    } else if (edgeRaw?.vlans && edgeRaw.vlans.length > 0) {
      // Limite duro: mais de 3 VLANs polui a label — mostra só contagem.
      // As VLANs individuais já aparecem no source/target label como V100,200+
      if (edgeRaw.vlans.length <= 3) {
        parts.push('VL ' + edgeRaw.vlans.join(','));
      } else {
        parts.push(`${edgeRaw.vlans.length} VLANs`);
      }
    }
  }

  // OSPF: mostra área(s) quando edge tem ospf_adj
  if (showL3 && edgeRaw?.ospf) {
    const o = edgeRaw.ospf;
    const areaA = o.area_a;
    const areaB = o.area_b;
    if (areaA && areaB && areaA === areaB) parts.push(`OSPF area ${areaA}`);
    else if (areaA || areaB) parts.push(`OSPF ${areaA || '?'} ↔ ${areaB || '?'}`);
    else parts.push('OSPF');
  }

  if (showL3 && edgeRaw?.bgp) {
    // Mostra AS_local → AS_peer pra operador identificar a sessão de uma vez
    // (ao invés de só 'bgp'/'ebgp'). Description vai no fim quando existe.
    const bgp = edgeRaw.bgp;
    const selfAs = bgp.self_as;
    const peerAs = bgp.peer_as;
    const session = bgp.session || 'bgp';
    const desc = bgp.description || '';
    let label = session;
    if (selfAs && peerAs) label = `${session} AS${selfAs} → AS${peerAs}`;
    else if (peerAs) label = `${session} → AS${peerAs}`;
    if (desc) label += ` · ${desc}`;
    parts.push(label);
  }

  // MPLS: mostra info quando qualquer evidência existe —
  //   1) VRF explícito no endpoint (binding L3VPN/CE-facing)
  //   2) Ambos os nodes têm has_mpls=true (transport P/PE)
  // Formato: "MPLS" ou "MPLS · vrfA" ou "MPLS · vrfA ↔ vrfB"
  const epA = edgeRaw?.endpoints?.a || {};
  const epB = edgeRaw?.endpoints?.b || {};
  const vrfA = epA.vrf;
  const vrfB = epB.vrf;
  const srcHasMpls = edgeRaw?.src_has_mpls === true;
  const tgtHasMpls = edgeRaw?.tgt_has_mpls === true;
  if (showL3 && (vrfA || vrfB || (srcHasMpls && tgtHasMpls))) {
    let mplsLabel = 'MPLS';
    if (vrfA || vrfB) {
      const vrfs = [];
      if (vrfA) vrfs.push(vrfA);
      if (vrfB && vrfB !== vrfA) vrfs.push(vrfB);
      mplsLabel = `MPLS · ${vrfs.join(' ↔ ')}`;
    }
    parts.push(mplsLabel);
  }

  // Tráfego sempre (overlay operacional — se existir)
  if (trafficEntry) {
    const inBps = trafficEntry.in_bps;
    const outBps = trafficEntry.out_bps;
    const hasAny = inBps != null || outBps != null;
    if (hasAny) {
      const items = [];
      if (outBps != null) items.push(`→ ${formatBps(outBps)}`);
      if (inBps != null)  items.push(`← ${formatBps(inBps)}`);
      parts.push(items.join('  '));
    }
  }
  return parts.join('\n');
}


/**
 * Converte payload do grafo pra elementos Cytoscape.
 */
/**
 * Enquadra a seleção SÓ se ela não couber no que já está visível.
 *
 * `cy.fit()` incondicional era o que resetava o zoom: escolher uma VLAN, um
 * anel ou uma interface jogava a câmera pra longe mesmo quando o resultado
 * já estava na tela — e quem tinha acabado de dar zoom num trecho perdia o
 * lugar. Ajudar a achar o que está fora é útil; mexer no enquadramento de
 * quem já estava olhando o alvo, não.
 */
function ajustarSeForaDaTela(cy, col, padding = 120) {
  if (!col || col.length === 0) return;
  const bb = col.boundingBox();
  const ext = cy.extent();
  const dentro = bb.x1 >= ext.x1 && bb.x2 <= ext.x2
    && bb.y1 >= ext.y1 && bb.y2 <= ext.y2;
  if (dentro) return;
  cy.animate({ fit: { eles: col, padding } }, { duration: 300 });
}


function graphToCyElements(graph, viewMode = 'radial', unfoldParallel = false, fillSysmapEdges = false) {
  if (!graph) return { elements: [], usePreset: false };
  const iconsMap = graph.icons || {};
  const allNodes = graph.nodes || [];
  const allEdges = graph.edges || [];

  const usePreset = viewMode === 'zabbix';
  const visibleNodes = usePreset
    ? allNodes.filter((n) => n.x !== undefined && n.y !== undefined && n.x !== null && n.y !== null)
    : allNodes;
  const visibleIds = new Set(visibleNodes.map((n) => String(n.id)));

  const nodes = visibleNodes.map((n) => {
    const iconUrl = n.iconid && iconsMap[String(n.iconid)] ? iconsMap[String(n.iconid)] : undefined;
    const data = {
      id: String(n.id),
      label: ((n.label || '').slice(0, 30)) + (n.mgmt_ip ? `\n${n.mgmt_ip}` : ''),
      vendor: n.vendor,
      role: n.role,
      mgmt_ip: n.mgmt_ip,
      asn: n.asn,
      issues_count: n.issues_count || 0,
      hostid_zabbix: n.hostid_zabbix,
      backup_status: n.backup_status || 'unknown',
      has_bgp: n.has_bgp === true,
      has_ospf: n.has_ospf === true,
      has_mpls: n.has_mpls === true,
      has_vlans: n.has_vlans === true,
      vlans: n.vlans || [],
      is_virtual: n.is_virtual === true,
      host_type: n.host_type || null,
      is_broadcast_domain: n.is_broadcast_domain === true,
      subnet: n.subnet,
      member_count: n.member_count,
      // Label do segmento traz a contagem: "10.200.77.16/28 · 10 equip."
      // O número é a informação que importa — diz o tamanho do domínio de
      // broadcast sem precisar clicar.
      segLabel: n.is_broadcast_domain === true && n.member_count
        ? `${n.label} · ${n.member_count} equip.`
        : n.label,
      bgp_role: n.bgp_role || 'none',
      ospf_role: n.ospf_role || 'none',
      // Schema v2 — IDs dos rings onde este host é membro
      ring_ids: (n.erps_rings || []).map((r) => r.ring_id),
      // Carrega as structs completas pro drawer/popup consumir
      bgp_peers_detail: n.bgp_peers || [],
      bgp_networks: n.bgp_networks || [],
      bgp_networks_v6: n.bgp_networks_v6 || [],
      bgp_redistribute: n.bgp_redistribute || [],
      ospf_global: n.ospf_global || null,
      ospf_areas: n.ospf_areas || [],
      mpls_global: n.mpls_global || null,
      mpls_l3vpns: n.mpls_l3vpns || [],
      mpls_l2vpns: n.mpls_l2vpns || [],
      vlans_detailed: n.vlans_detailed || [],
      erps_rings: n.erps_rings || [],
      isis_global: n.isis_global || null,
      route_policies: n.route_policies || {},
      router_id: n.router_id,
      lag_bundles: n.lag_bundles || {},
      // Lista compacta de interfaces — pro drawer cruzar com áreas OSPF, VRFs, trunks
      interfaces_detail: n.interfaces || [],
    };
    if (iconUrl) data.iconUrl = iconUrl;
    const out = { data };
    if (usePreset) out.position = { x: n.x, y: n.y };
    return out;
  });

  const edges = allEdges
    .filter((e) => visibleIds.has(String(e.source)) && visibleIds.has(String(e.target)))
    .flatMap((e) => {
      const kinds = e.kinds || [];
      const cls = kinds.map((k) => `kind-${k}`);
      // Enriquece o raw com has_mpls dos 2 endpoints pra buildEdgeLabel
      // saber quando os 2 lados são MPLS (mesmo sem VRF nos endpoints).
      const srcNode = nodes.find((nd) => nd.data.id === String(e.source));
      const tgtNode = nodes.find((nd) => nd.data.id === String(e.target));
      e.src_has_mpls = srcNode?.data?.has_mpls === true;
      e.tgt_has_mpls = tgtNode?.data?.has_mpls === true;
      // Aresta que toca um segmento é PERTENCIMENTO, não enlace — ganha
      // classe própria pra ficar fina e apagada.
      if (srcNode?.data?.is_broadcast_domain || tgtNode?.data?.is_broadcast_domain) {
        cls.push('para-segmento');
      }
      if (e.issues && e.issues.length > 0) cls.push('has-issue');
      if (kinds.length >= 3) cls.push('bundled', 'bundled-heavy');
      else if (kinds.length === 2) cls.push('bundled');

      // Helper pra montar labels de um único endpoint pair (iface+ip)
      const buildEndpointLabels = (ep, subnet) => {
        const aBest = ep.a?.physical_iface || ep.a?.iface;
        const bBest = ep.b?.physical_iface || ep.b?.iface;
        const aPhyPrefix = ep.a?.physical_iface && ep.a?.iface
          ? abbreviateIface(ep.a.physical_iface)
          : ifaceOrFallback(aBest, e, 'a');
        const bPhyPrefix = ep.b?.physical_iface && ep.b?.iface
          ? abbreviateIface(ep.b.physical_iface)
          : ifaceOrFallback(bBest, e, 'b');
        let aLabel = (ep.a?.physical_iface && ep.a?.iface && ep.a.physical_iface !== ep.a.iface)
          ? `${aPhyPrefix}:${abbreviateIface(ep.a.iface)}`
          : aPhyPrefix;
        let bLabel = (ep.b?.physical_iface && ep.b?.iface && ep.b.physical_iface !== ep.b.iface)
          ? `${bPhyPrefix}:${abbreviateIface(ep.b.iface)}`
          : bPhyPrefix;
        if (subnet && ep.a?.ip) aLabel = `${aLabel} ${ep.a.ip}`;
        if (subnet && ep.b?.ip) bLabel = `${bLabel} ${ep.b.ip}`;
        return [aLabel, bLabel];
      };

      // Unfold mode: gera 1 Cytoscape edge por par de iface/subnet distinto.
      // Só desdobra se existirem 2+ links paralelos distintos no payload — senão
      // cai no caminho folded (1 edge, comportamento legacy).
      const parallel = e.parallel_links || [];
      // Dedup por (a_iface, b_iface, subnet) — ignora kind pra não duplicar
      // quando bgp + p2p estão no mesmo link.
      const distinctKey = (p) => `${p.a_iface}|${p.b_iface}|${p.subnet}`;
      const uniqueLinks = [];
      const seen = new Set();
      for (const p of parallel) {
        const k = distinctKey(p);
        if (seen.has(k)) continue;
        seen.add(k);
        uniqueLinks.push(p);
      }

      // Unfold waterfall (prioridade por camada — opera no que tiver multiplicidade):
      //   L1: parallel_links > 1      → 1 linha por sub-iface/subnet (kind-p2p, etc)
      //   L3: kinds > 1               → 1 linha por protocolo (bgp, ospf, mpls)
      //   L2: vlans > 1               → 1 linha por VLAN (kind-trunk)
      // Cada child herda classe kind-X então mantém cor do protocolo dela.

      // Auto-unfold por KIND quando bundled (2+ protocolos mesma conexão).
      // Estratégia "rail stack": linhas paralelas com offset PEQUENO (8-14px)
      // pra parecer empilhadas, não em leque. Cada linha solid colorida pelo
      // protocolo dela. L1 mais grosso (cable base) por baixo, L3 mais fino
      // por cima via z-index. Label só na 1ª linha pra não duplicar info.
      const shouldUnfoldByKind = (
        uniqueLinks.length <= 1
        && kinds.length > 1
      );
      if (shouldUnfoldByKind) {
        const ep = e.endpoints || {};
        // Ordena kinds por layer: L1 (p2p) < L2 (trunk/shared) < L3 (proto)
        // pra L3 ficar "em cima" no z-index.
        const KIND_LAYER = {
          p2p: 1, trunk: 2, shared_lan: 2, l2_desc: 2,
          ospf_adj: 3, static_route: 3, bgp: 4, mpls: 5,
        };
        const ordered = [...kinds].sort((a, b) =>
          (KIND_LAYER[a] || 99) - (KIND_LAYER[b] || 99));
        const total = ordered.length;
        const spacing = 11;  // apertado — parece stack, não leque
        const KIND_WIDTH = {
          p2p: 5, trunk: 4.5, shared_lan: 4, l2_desc: 4,
          ospf_adj: 3, static_route: 2.5, bgp: 2.5,
        };
        return ordered.map((k, idx) => {
          const controlDistance = Math.round((idx - (total - 1) / 2) * spacing);
          const [aLab, bLab] = buildEndpointLabels(ep, e.subnet);
          const childRaw = {
            id: `${e.id}:k${k}`,
            source: e.source, target: e.target,
            kinds: [k], layers: e.layers || [],
            endpoints: ep,
            subnet: e.subnet,
            vlans: e.vlans || [],
            bgp: k === 'bgp' ? e.bgp : null,
            ospf: k === 'ospf_adj' ? e.ospf : null,
            lag: e.lag,
            confidence: e.confidence,
            issues: [],
            src_has_mpls: e.src_has_mpls,
            tgt_has_mpls: e.tgt_has_mpls,
          };
          // Só a PRIMEIRA child do stack carrega middle label + endpoint
          // labels. As outras vêm vazias pra não empilhar texto duplicado.
          const isFirst = idx === 0;
          return {
            data: {
              id: `${e.id}:k${k}`,
              source: String(e.source),
              target: String(e.target),
              kinds: [k],
              layers: e.layers || [],
              vlans: e.vlans || [],
              edgeLabel: isFirst ? buildEdgeLabel(childRaw, null) : '',
              sourceIfaceLabel: isFirst ? aLab : '',
              targetIfaceLabel: isFirst ? bLab : '',
              confidence: e.confidence,
              subnet: e.subnet,
              issues: [],
              raw: childRaw,
              is_unfold_child: true,
              parent_edge_id: e.id,
              controlDistance,
              widthOverride: KIND_WIDTH[k] || 2.5,
              zIdx: KIND_LAYER[k] || 1,
            },
            classes: [`kind-${k}`, 'unfolded', 'auto-stack'],
          };
        });
      }

      // L2: unfold por VLAN quando nenhum parallel nem kind múltiplo
      const shouldUnfoldByVlan = (
        unfoldParallel
        && uniqueLinks.length <= 1
        && kinds.length <= 1
        && (e.vlans || []).length > 1
      );
      if (shouldUnfoldByVlan) {
        const ep = e.endpoints || {};
        const vlanList = e.vlans || [];
        const total = vlanList.length;
        // Spacing: 28px mínimo, escala pra edges compridas.
        // Ponto médio em offset 0, primeira metade negativa, segunda positiva.
        const spacing = total > 10 ? 22 : 32;
        return vlanList.map((vid, idx) => {
          const controlDistance = Math.round((idx - (total - 1) / 2) * spacing);
          const subEp = {
            a: { ...ep.a, iface: `Vlan${vid}`, physical_iface: ep.a?.physical_iface || ep.a?.iface },
            b: { ...ep.b, iface: `Vlan${vid}`, physical_iface: ep.b?.physical_iface || ep.b?.iface },
          };
          const [aLab, bLab] = buildEndpointLabels(subEp, e.subnet);
          return {
            data: {
              id: `${e.id}:v${vid}`,
              source: String(e.source),
              target: String(e.target),
              kinds: ['trunk'],
              layers: ['L2'],
              vlans: [vid],
              edgeLabel: `V${vid}${e.subnet ? '\n' + e.subnet : ''}`,
              sourceIfaceLabel: aLab,
              targetIfaceLabel: bLab,
              confidence: e.confidence,
              subnet: e.subnet,
              issues: [],
              raw: { ...e, endpoints: subEp, vlans: [vid], kinds: ['trunk'] },
              is_unfold_child: true,
              parent_edge_id: e.id,
              controlDistance,
            },
            classes: ['kind-trunk', 'unfolded'],
          };
        });
      }

      if (unfoldParallel && uniqueLinks.length > 1) {
        const totalLinks = uniqueLinks.length;
        const linkSpacing = totalLinks > 10 ? 22 : 32;
        return uniqueLinks.map((p, idx) => {
          const controlDistance = Math.round((idx - (totalLinks - 1) / 2) * linkSpacing);
          // Endpoint específico deste link — iface/ip só dele, NÃO replica o
          // set agregado do parent (senão todas as 3 linhas mostram os mesmos
          // "VL 666,1000"). Cada sub-edge fica com vlans=[] e edgeLabel só
          // com subnet/bgp/session daquele link específico.
          const ep = {
            a: {
              host_id: String(e.source),
              iface: p.a_iface,
              ip: p.a_ip,
              physical_iface: null,
            },
            b: {
              host_id: String(e.target),
              iface: p.b_iface,
              ip: p.b_ip,
              physical_iface: null,
            },
          };
          const [aLab, bLab] = buildEndpointLabels(ep, p.subnet);
          const subKind = p.kind || kinds[0] || 'p2p';
          // Monta um raw "estéril" só com os campos desse link —
          // nada de vlans agregadas, nada de bgp_meta do parent que era
          // compartilhado entre todos os raws folded.
          const childRaw = {
            id: `${e.id}:u${idx}`,
            source: e.source,
            target: e.target,
            kinds: [subKind],
            layers: e.layers || [],
            endpoints: ep,
            subnet: p.subnet,
            vlans: [],
            bgp: subKind === 'bgp' ? e.bgp : null,
            ospf: subKind === 'ospf_adj' ? e.ospf : null,
            lag: null,
            confidence: e.confidence,
            issues: [],
          };
          return {
            data: {
              id: `${e.id}:u${idx}`,
              source: String(e.source),
              target: String(e.target),
              kinds: [subKind],
              layers: e.layers || [],
              vlans: [],
              edgeLabel: buildEdgeLabel(childRaw, null),
              sourceIfaceLabel: aLab,
              targetIfaceLabel: bLab,
              confidence: e.confidence,
              subnet: p.subnet,
              issues: [],
              raw: childRaw,
              is_unfold_child: true,
              parent_edge_id: e.id,
              controlDistance,
            },
            classes: [`kind-${subKind}`, 'unfolded'],
          };
        });
      }

      // Caminho legacy (folded)
      const ep = e.endpoints || {};
      const [aLabel, bLabel] = buildEndpointLabels(ep, e.subnet);
      // Cor por subnet quando for shared_lan (cada broadcast domain fica
      // com uma cor distinta pro operador distinguir visualmente).
      const sharedColor = kinds.includes('shared_lan') && e.subnet
        ? colorForString(e.subnet) : null;
      return [{
        data: {
          id: e.id,
          source: String(e.source),
          target: String(e.target),
          kinds,
          layers: e.layers || [],
          vlans: e.vlans || [],
          edgeLabel: buildEdgeLabel(e, null),
          sourceIfaceLabel: aLabel,
          targetIfaceLabel: bLabel,
          confidence: e.confidence,
          subnet: e.subnet,
          sharedColor,
          issues: e.issues || [],
          raw: e,
        },
        classes: cls,
      }];
    });

  // Completa edges faltantes do Zabbix sysmap (quando o operador desenhou
  // links manualmente no Zabbix que não batem com nossa inferência). Só
  // aplica em zabbix mode e quando fillSysmapEdges está ligado.
  if (usePreset && fillSysmapEdges) {
    const sysmapLinks = graph.sysmap_links || [];
    // Index de hostid_zabbix → node.id (interno) pra mapear os selements
    const zbxToInternal = new Map();
    for (const n of visibleNodes) {
      if (n.hostid_zabbix) zbxToInternal.set(String(n.hostid_zabbix), String(n.id));
    }
    // Set de pares já cobertos pela inferência — não queremos duplicar
    const existingPairs = new Set();
    for (const ed of edges) {
      const a = ed.data.source; const b = ed.data.target;
      existingPairs.add([a, b].sort().join('|'));
    }
    let sysCounter = 0;
    for (const lk of sysmapLinks) {
      const srcInternal = zbxToInternal.get(String(lk.host_a));
      const tgtInternal = zbxToInternal.get(String(lk.host_b));
      if (!srcInternal || !tgtInternal) continue;
      const pairKey = [srcInternal, tgtInternal].sort().join('|');
      if (existingPairs.has(pairKey)) continue;
      sysCounter += 1;
      edges.push({
        data: {
          id: `sysmap-${sysCounter}`,
          source: srcInternal,
          target: tgtInternal,
          kinds: ['sysmap'],
          layers: [],
          vlans: [],
          edgeLabel: lk.label || '',
          sourceIfaceLabel: '',
          targetIfaceLabel: '',
          confidence: 'medium',
          subnet: null,
          issues: [],
          raw: { kinds: ['sysmap'], endpoints: { a: {}, b: {} }, vlans: [], subnet: null },
          is_sysmap_edge: true,
          sysmap_triggerids: lk.triggerids || [],
          sysmap_color: lk.color,
        },
        classes: ['kind-sysmap', 'sysmap-edge'],
      });
      existingPairs.add(pairKey);
    }
  }

  // shared_lan fica como edges cruas (dashed teal com sharedColor per subnet).
  return { elements: [...nodes, ...edges], usePreset };
}


function makeStylesheet(theme) {
  const isDark = theme.palette.mode === 'dark';
  const textColor = isDark ? '#f1f5f9' : '#f8fafc';
  const textOutline = isDark ? '#0f172a' : '#1e293b';
  const labelBg = isDark ? '#0f172a' : '#1e293b';

  return [
    {
      selector: 'node',
      style: {
        'background-color': '#7f8c8d',
        'label': 'data(label)',
        'color': textColor,
        'font-size': '15px',
        'font-weight': 'bold',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'text-wrap': 'wrap',
        'text-max-width': '200px',
        'text-margin-y': 6,
        'text-outline-width': 3,
        'text-outline-color': textOutline,
        'width': 72,
        'height': 72,
        'border-width': 2,
        'border-color': '#333',
        'shape': 'round-rectangle',
      },
    },
    ...Object.entries(VENDOR_COLOR).map(([vendor, color]) => ({
      selector: `node[vendor = "${vendor}"]`,
      style: { 'background-color': color },
    })),
    {
      selector: 'node[iconUrl]',
      style: {
        'background-image': 'data(iconUrl)',
        'background-fit': 'contain',
        'background-clip': 'none',
        'background-opacity': 0,
        'background-color': 'transparent',
        'border-width': 0,
        'width': 80,
        'height': 80,
        'shape': 'rectangle',
        'font-size': '15px',
      },
    },
    { selector: 'node[role = "border"]', style: { shape: 'hexagon', width: 80, height: 80 } },
    { selector: 'node[role = "core"]',   style: { shape: 'diamond', width: 72, height: 72 } },
    { selector: 'node[role = "olt"]',    style: { shape: 'round-octagon' } },

    // Virtual BGP externos — "nuvem" de AS
    { selector: 'node[?is_virtual]',
      style: {
        'background-color': '#1e293b',
        'background-opacity': 0.9,
        'border-color': '#e67e22',
        'border-width': 3,
        'border-style': 'dashed',
        'shape': 'round-tag',
        'width': 90,
        'height': 60,
        'color': '#f59e0b',
        'font-size': '13px',
      } },
    // Nó de SEGMENTO (domínio de broadcast). Vem depois do bloco is_virtual
    // de propósito: o segmento também é is_virtual e precisa sobrescrever.
    //
    // Semântica: não é equipamento, é "estes N equipamentos compartilham a
    // sub-rede X". Por isso forma e cor próprias, apagadas — ele é contexto,
    // não protagonista. Antes o mapa ou ligava todos com todos (afirmando
    // adjacência que não existe) ou descartava a sub-rede inteira e os
    // equipamentos sumiam.
    { selector: 'node[?is_broadcast_domain]',
      style: {
        'shape': 'round-diamond',
        'background-color': '#334155',
        'background-opacity': 0.85,
        'border-color': '#64748b',
        'border-width': 1.5,
        'border-style': 'solid',
        'width': 34,
        'height': 34,
        'label': 'data(label)',
        'color': '#94a3b8',
        'font-size': '10px',
        'font-weight': 'normal',
        'text-valign': 'bottom',
        'text-margin-y': 3,
        'text-outline-width': 2,
        'text-outline-color': labelBg,
      } },
    // Contagem de membros ao lado do CIDR, quando o backend informar
    { selector: 'node[?is_broadcast_domain][member_count]',
      style: { 'label': 'data(segLabel)' } },
    // Aresta até o segmento = PERTENCIMENTO, não cabo. Fina, apagada e sem
    // seta, pra não competir com enlace de verdade.
    { selector: 'edge.para-segmento',
      style: {
        'line-color': '#475569',
        'line-style': 'solid',
        'width': 1,
        'opacity': 0.55,
        'target-arrow-shape': 'none',
        'label': '',
      } },
    // Segmento nunca recebe borda de alarme: não é equipamento, não tem estado
    { selector: 'node[?is_broadcast_domain].alarm-disaster, node[?is_broadcast_domain].alarm-high',
      style: { 'border-color': '#64748b', 'border-width': 1.5, 'overlay-opacity': 0 } },

    // Unfold mode: edges paralelas entre o mesmo par (u,v) usam
    // unbundled-bezier com control-point-distances derivado do índice
    // (data(controlDistance)) pra cada linha ganhar offset próprio.
    { selector: 'edge.unfolded',
      style: {
        'curve-style': 'unbundled-bezier',
        'control-point-distances': 'data(controlDistance)',
        'control-point-weights': 0.5,
        'opacity': 1,
      } },
    // Auto-stack: bundled unfolded por kind ganha width data-bound (L1 cable
    // grosso, L3 fino) + z-index pra L3 ficar sempre por cima do L1. Tipo
    // "trilhos" empilhados — operador vê todos os protocolos sem confundir.
    { selector: 'edge.auto-stack',
      style: {
        'width': 'data(widthOverride)',
        'z-index': 'data(zIdx)',
      } },
    // Sysmap supplemental edges — links desenhados manualmente no Zabbix
    // sysmap que não têm correspondente na nossa inferência. Cinza tracejado
    // pro operador saber que é "só Zabbix disse", não é evidência de config.
    { selector: 'edge.kind-sysmap',
      style: {
        'line-style': 'dashed',
        'line-color': '#94a3b8',
        'width': 1.5,
        'color': '#cbd5e1',
        'opacity': 0.7,
        'target-arrow-shape': 'none',
      } },
    // shared_lan com cor distinta por subnet (data-bound). Cada broadcast
    // domain vira uma cor independente pro operador distinguir visualmente
    // qual link pertence a qual /28/29/24. Override no .kind-shared_lan base.
    { selector: 'edge.kind-shared_lan[sharedColor]',
      style: {
        'line-color': 'data(sharedColor)',
        'color': 'data(sharedColor)',
        'line-style': 'dashed',
      } },

    // Alarmes (severidade) — borda colorida + overlay colorido no ícone (igual Zabbix)
    { selector: 'node.alarm-disaster',
      style: {
        'border-color': '#dc2626', 'border-width': 6,
        'overlay-color': '#dc2626', 'overlay-opacity': 0.35, 'overlay-padding': 4,
      } },
    { selector: 'node.alarm-high',
      style: {
        'border-color': '#ea580c', 'border-width': 5,
        'overlay-color': '#ea580c', 'overlay-opacity': 0.3, 'overlay-padding': 3,
      } },
    { selector: 'node.alarm-average',
      style: {
        'border-color': '#ca8a04', 'border-width': 4,
        'overlay-color': '#eab308', 'overlay-opacity': 0.22, 'overlay-padding': 2,
      } },

    // Implodir folhas — nodes e edges escondidos
    { selector: '.implode-hidden', style: { 'display': 'none' } },
    // Filtro por tipo de host
    { selector: '.host-type-filtered', style: { 'display': 'none' } },


    // Session state overlay — edges BGP/OSPF piscam verde (established) ou
    // vermelho (down/idle). Override sobre as cores de kind.
    { selector: 'edge.session-up',
      style: {
        'line-color': '#22c55e',
        'color': '#86efac',
      } },
    { selector: 'edge.session-down',
      style: {
        'line-color': '#ef4444',
        'color': '#fca5a5',
        'width': 4,
        'line-style': 'dashed',
      } },

    // Node metrics threshold — glow suave quando CPU/MEM/Temp elevados
    { selector: 'node.metrics-warn',
      style: {
        'border-color': '#eab308',
        'border-width': 3,
      } },
    { selector: 'node.metrics-critical',
      style: {
        'border-color': '#ef4444',
        'border-width': 4,
        'overlay-color': '#ef4444',
        'overlay-opacity': 0.2,
        'overlay-padding': 3,
      } },

    // Backup status — borda diferente
    { selector: 'node[backup_status = "missing"]',
      style: { 'border-color': '#e74c3c', 'border-style': 'dashed', 'border-width': 3 } },
    { selector: 'node[backup_status = "stale"]',
      style: { 'border-color': '#e74c3c', 'border-style': 'dotted', 'border-width': 2 } },
    { selector: 'node[backup_status = "old"]',
      style: { 'border-color': '#f39c12', 'border-style': 'dotted', 'border-width': 2 } },

    // Issues badge
    { selector: 'node[issues_count > 0]', style: { 'border-style': 'double' } },

    // Edges base
    {
      selector: 'edge',
      style: {
        'width': 2,
        'line-color': '#95a5a6',
        'target-arrow-shape': 'none',
        'curve-style': 'bezier',
        'opacity': 0.9,
        'label': 'data(edgeLabel)',
        'font-size': '11px',
        'font-weight': 'bold',
        'color': '#95a5a6',
        'text-wrap': 'wrap',
        'text-max-width': '200px',
        'text-outline-width': 2,
        'text-outline-color': labelBg,
        'text-background-color': labelBg,
        'text-background-opacity': 0.85,
        'text-background-padding': '3px',
        'text-background-shape': 'roundrectangle',
        // Texto do meio da linha posicionado ACIMA da edge (margin-y negativo
        // = pra cima no sistema Cytoscape). Evita o texto pintar em cima do
        // pontilhado/listras e ficar ilegível.
        'text-rotation': 'autorotate',
        'text-margin-y': -14,
        // Labels compactas nos endpoints (nome de iface abreviado).
        // text-offset afasta o label da borda do nó ao longo da edge — 60px
        // comporta labels grandes tipo "Eth-Trunk1|V3005" ou "AS262379" sem
        // sobrepor o ícone quando tem 6+ conexões concorrendo por espaço.
        'source-label': 'data(sourceIfaceLabel)',
        'target-label': 'data(targetIfaceLabel)',
        'source-text-offset': 60,
        'target-text-offset': 60,
        'source-text-margin-y': -2,
        'target-text-margin-y': -2,
        'source-text-rotation': 'autorotate',
        'target-text-rotation': 'autorotate',
        'font-family': 'monospace',
      },
    },
    // Estilo compartilhado pras source/target labels (precisa ser no seletor
    // `edge` de novo pra aplicar só aos source/target text, usando propriedades)
    {
      selector: 'edge',
      style: {
        // Força tamanho menor nas labels de endpoint (cytoscape não tem style
        // separado por label — usamos o mesmo font-size)
      },
    },
    // Cores por kind — cada layer tem cor própria pra troubleshoot direto.
    //
    // L1 (físico)  : p2p cyan
    // L2 (vlan)    : trunk + shared_lan teal (shared_lan override sharedColor por subnet)
    // L3 (routing) : BGP orange, OSPF sky, MPLS fuchsia overlay, Static slate
    //
    // Striping (.striped) entra quando edge tem 2+ kinds — alterna as cores
    // de TODOS os layers presentes (L1+L2+L3 misturados se for o caso).
    //
    // Convenção de line-style:
    //   solid  → adjacência certa
    //   dashed → bundled (multi-protocolo) ou shared_lan ambíguo
    //   dotted → heurística fraca (l2_desc)
    //   dashed vermelho pulsante → sessão DOWN
    { selector: 'edge.kind-p2p',
      style: { 'line-style': 'solid',  'line-color': '#06b6d4', 'width': 2.5,   // cyan (L1)
               'color': '#67e8f9', 'text-outline-color': labelBg } },
    { selector: 'edge.kind-trunk',
      style: { 'line-style': 'solid',  'line-color': '#14b8a6', 'width': 3,     // teal (L2)
               'color': '#5eead4', 'text-outline-color': labelBg } },
    { selector: 'edge.kind-shared_lan',
      style: { 'line-style': 'dashed', 'line-color': '#14b8a6', 'width': 2,     // teal dashed (L2 ambíguo)
               'color': '#5eead4', 'text-outline-color': labelBg } },
    { selector: 'edge.kind-l2_desc',
      style: { 'line-style': 'dotted', 'line-color': '#64748b', 'opacity': 0.5, // slate dim (L2 heurístico)
               'color': '#94a3b8', 'text-outline-color': labelBg } },
    { selector: 'edge.kind-ospf_adj',
      style: { 'line-style': 'solid',  'line-color': '#0ea5e9', 'width': 3,     // sky (L3 IGP)
               'color': '#7dd3fc', 'text-outline-color': labelBg } },
    { selector: 'edge.kind-bgp',
      style: { 'line-style': 'solid',  'line-color': '#f97316', 'width': 2.5,   // orange (L3 EGP)
               'color': '#fb923c', 'text-outline-color': labelBg } },
    { selector: 'edge.kind-static_route',
      style: { 'line-style': 'dotted', 'line-color': '#64748b', 'width': 1.5,   // slate (L3 manual)
               'color': '#cbd5e1', 'text-outline-color': labelBg } },

    // MPLS overlay — fuchsia sólido. DEFINIDO APÓS todos os kind-* pra que
    // o line-color sobrescreva o do kind. Importante: ordem CSS no array
    // determina precedência (mesma especificidade, último vence).
    { selector: 'edge.mpls-overlay',
      style: {
        'line-color': '#d946ef',
        'color': '#f0abfc',
        'width': 3,
      } },

    // Edges com tráfego real (width proporcional)
    { selector: 'edge.has-traffic',
      style: {
        'width': 'data(trafficWidth)',
        'line-color': '#06b6d4',
        'color': '#67e8f9',
      } },

    // Bundled: 2+ kinds agregadas no mesmo par. Combinada com .striped
    // (data-bound gradient) cria listras alternadas das cores dos protocolos.
    { selector: 'edge.bundled',
      style: { 'line-style': 'dashed', 'width': 3 } },
    { selector: 'edge.bundled-heavy',
      style: { 'line-style': 'dashed', 'width': 3.5 } },

    // Striped: edges com 2+ kinds ganham gradient com cores alternadas dos
    // protocolos. Operador vê BGP+OSPF como listras laranja|sky em vez de
    // linha sólida cor do dominante. Funciona em conjunto com .bundled.
    { selector: 'edge.striped',
      style: {
        'line-fill': 'linear-gradient',
        'line-gradient-stop-colors': 'data(gradientColors)',
        'line-gradient-stop-positions': 'data(gradientPositions)',
      } },

    // Issues
    { selector: 'edge.has-issue',
      style: { 'line-color': '#e74c3c', 'width': 3.5,
               'color': '#f1948a', 'text-outline-color': labelBg } },

    // Filtros (hidden = filter off)
    { selector: '.hidden', style: { display: 'none' } },
    { selector: '.host-filtered-out', style: { display: 'none' } },

    // ILUMINAR VLAN ---------
    { selector: '.vlan-dimmed', style: { 'opacity': 0.08, 'text-opacity': 0.1 } },
    { selector: 'node.vlan-lit',
      style: {
        'border-color': '#fbbf24',
        'border-width': 8,
        'overlay-color': '#fbbf24',
        'overlay-opacity': 0.35,
        'overlay-padding': 6,
      } },
    { selector: 'edge.vlan-edge-lit',
      style: {
        'line-color': '#fbbf24',
        'line-style': 'dashed',
        'line-dash-pattern': [10, 6],
        'width': 6,
        'color': '#fbbf24',
        'overlay-color': '#fbbf24',
        'overlay-opacity': 0.2,
        'overlay-padding': 3,
      } },

    // L1 ONLY mode: esconde edges não-físicas ---------
    { selector: 'edge.l1-hidden', style: { 'display': 'none' } },

    // VIEW MODE (genérico — L1/L2/L3/Serviços) ---------
    { selector: '.view-hidden', style: { 'display': 'none' } },
    { selector: '.view-dimmed', style: { 'opacity': 0.08, 'text-opacity': 0.1 } },
    // Solo focus: edges destacadas quando 1 filtro está ativo solo. Width
    // boost + opacity full + glow leve pra puxar atenção visual.
    { selector: 'edge.solo-focus',
      style: { 'opacity': 1, 'width': 4 } },
    { selector: 'node.health-lit',
      style: {
        'border-color': '#ef4444',
        'border-width': 8,
        'overlay-color': '#ef4444',
        'overlay-opacity': 0.4,
        'overlay-padding': 5,
      } },
    { selector: 'edge.health-lit',
      style: {
        'line-color': '#ef4444',
        'width': 5,
        'overlay-color': '#ef4444',
        'overlay-opacity': 0.3,
      } },

    // LAG PHANTOM EDGES (quando expand mode ativo) ---------
    { selector: 'edge.lag-phantom',
      style: {
        'line-color': '#60a5fa',
        'line-style': 'solid',
        'width': 2,
        'opacity': 0.85,
        'curve-style': 'bezier',
        'control-point-step-size': 40,
        'target-arrow-shape': 'none',
        'color': '#60a5fa',
      } },
    { selector: 'edge.lag-expanded',
      style: {
        'line-style': 'dashed',
        'line-dash-pattern': [4, 4],
        'opacity': 0.3,
      } },

    // PATH TRACE ---------
    { selector: '.path-dimmed', style: { 'opacity': 0.08, 'text-opacity': 0.1 } },
    { selector: 'node.path-trace',
      style: {
        'border-color': '#60a5fa',
        'border-width': 6,
        'overlay-color': '#60a5fa',
        'overlay-opacity': 0.25,
        'overlay-padding': 4,
      } },
    { selector: 'node.path-endpoint',
      style: {
        'border-color': '#22d3ee',
        'border-width': 10,
        'overlay-color': '#22d3ee',
        'overlay-opacity': 0.5,
        'overlay-padding': 8,
      } },
    { selector: 'edge.path-trace',
      style: {
        'line-color': '#60a5fa',
        'line-style': 'solid',
        'width': 6,
        'color': '#60a5fa',
        'overlay-color': '#60a5fa',
        'overlay-opacity': 0.25,
        'overlay-padding': 3,
        'target-arrow-shape': 'triangle',
        'target-arrow-color': '#60a5fa',
      } },

    // ILUMINAR ERPS RING ---------
    { selector: '.ring-dimmed', style: { 'opacity': 0.08, 'text-opacity': 0.1 } },
    { selector: 'node.ring-lit',
      style: {
        'border-color': '#06b6d4',
        'border-width': 8,
        'overlay-color': '#06b6d4',
        'overlay-opacity': 0.35,
        'overlay-padding': 6,
      } },
    { selector: 'node.ring-rpl-owner',
      style: {
        'border-color': '#f97316',
        'border-width': 10,
        'overlay-color': '#f97316',
        'overlay-opacity': 0.5,
        'overlay-padding': 8,
      } },
    { selector: 'edge.ring-edge-lit',
      style: {
        'line-color': '#06b6d4',
        'line-style': 'solid',
        'width': 7,
        'color': '#06b6d4',
        'overlay-color': '#06b6d4',
        'overlay-opacity': 0.25,
        'overlay-padding': 4,
      } },

    // Fade (click highlight)
    { selector: '.faded', style: { opacity: 0.12, 'text-opacity': 0.15 } },
    { selector: 'node.focused',
      style: { 'border-width': 6, 'border-color': '#fbbf24', 'overlay-opacity': 0.2 } },
    { selector: 'edge.focused',
      style: { 'width': 5, 'line-color': '#fbbf24' } },
    // Search match: borda ciano brilhante + glow
    { selector: 'node.search-match',
      style: {
        'border-width': 8,
        'border-color': '#22d3ee',
        'overlay-color': '#22d3ee',
        'overlay-opacity': 0.3,
        'overlay-padding': 8,
      } },

    { selector: 'node:selected', style: { 'border-color': '#fff', 'border-width': 4 } },
    { selector: 'edge:selected', style: { 'line-color': '#fff', 'width': 4 } },
  ];
}


/**
 * Layout estrela-quadrante:
 *   - Mapa dividido em 4 QUADRANTES (NW/NE/SW/SE)
 *   - Hostnames têm city token (sufixo 3-5 chars maiúsculas tipo FWN/TEP/ROB)
 *   - Cidades distribuídas em quadrantes por hash determinístico do nome
 *     (cidades com mesmo prefixo vizinhas)
 *   - Cada cidade vira uma ESTRELA local: router/switch central + OLTs/Radios
 *     em órbita ao redor (raio compacto ~250-400px)
 *   - BGP backbone fica no centro absoluto do mapa
 *   - Isolados em coluna lateral
 */
function computeRadialConstraints(cy, explosionLevel = 50, tuning = null) {
  if (!cy) return [];
  const scale = 0.5 + (explosionLevel / 50);
  const constraints = [];

  // Classificação inline.
  const typeOf = (data) => {
    const raw = (data.host_type || '').toUpperCase().trim();
    if (raw === 'OLT') return 'olt';
    if (raw === 'RADIO') return 'radio';
    if (raw) return 'switch_router';
    if (data.role === 'olt') return 'olt';
    const vendor = (data.vendor || '').toUpperCase();
    if (['VSOL', 'FIBERHOME', 'ZTE', 'CIANET', 'NOKIA', 'PARKS', 'FURUKAWA', 'ZYXEL'].includes(vendor)) return 'olt';
    return 'switch_router';
  };

  // Extrai city token do hostname. Padrões observados na prática:
  //   TCHT-SWL3-HW.S6720-FWN-EQ026   → FWN
  //   23021-OLT-FH-RTO               → RTO
  //   23021-NE20-RR-03-TEP           → TEP
  //   TtMir-F5n-C                    → MIR (3 chars maiúsculas iniciais)
  //   23021-Aligera-AG800-CMV        → CMV
  // Pega o último token alfa de 3-5 letras maiúsculas.
  const cityOf = (label) => {
    if (!label) return null;
    const tokens = label.toUpperCase().split(/[-_.\s]+/).filter(Boolean);
    // Procura do FIM pro INÍCIO o primeiro token alfa de 3-5 chars
    for (let i = tokens.length - 1; i >= 0; i -= 1) {
      const tk = tokens[i];
      if (/^[A-Z]{3,5}$/.test(tk)) return tk;
    }
    return null;
  };

  // Separa isolados (grau 0) — coluna lateral
  const isolated = [];
  const connected = [];
  cy.nodes().forEach((n) => {
    if (n.degree() === 0) isolated.push(n);
    else connected.push(n);
  });

  // Buckets por tipo
  const bgpHosts = [];
  const others = [];  // tudo o resto (sw/rtr/olt/radio)
  connected.forEach((n) => {
    const d = n.data();
    if (d.has_bgp === true && typeOf(d) === 'switch_router') bgpHosts.push(n);
    else others.push(n);
  });

  // BGP backbone no CENTRO — círculo pequeno
  const bgpRadius = 250 * scale;
  if (bgpHosts.length === 1) {
    constraints.push({ nodeId: bgpHosts[0].id(), position: { x: 0, y: 0 } });
  } else if (bgpHosts.length > 1) {
    bgpHosts.forEach((node, i) => {
      const a = (i / bgpHosts.length) * 2 * Math.PI;
      constraints.push({
        nodeId: node.id(),
        position: { x: bgpRadius * Math.cos(a), y: bgpRadius * Math.sin(a) },
      });
    });
  }

  // Agrupa OUTROS por city token. Sem city → cluster especial "MISC"
  const cityGroups = new Map();  // city → [node]
  others.forEach((n) => {
    const city = cityOf(n.data().label) || '__MISC__';
    if (!cityGroups.has(city)) cityGroups.set(city, []);
    cityGroups.get(city).push(n);
  });

  // Hash determinístico do city → quadrant (NE/NW/SE/SW)
  const cityHash = (s) => {
    let h = 0;
    for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) & 0x7fffffff;
    return h;
  };

  // Distribui cidades em 4 quadrantes baseado em hash, mas mantém estável
  // (mesma cidade sempre cai no mesmo quadrante).
  // Quadrantes (Cytoscape: y cresce pra BAIXO):
  //   NW: angle ∈ [π, 3π/2)    NE: angle ∈ [3π/2, 2π)
  //   SW: angle ∈ [π/2, π)     SE: angle ∈ [0, π/2)
  const quadrants = [
    { name: 'NE', startAngle: -Math.PI / 2, endAngle: 0 },
    { name: 'SE', startAngle: 0, endAngle: Math.PI / 2 },
    { name: 'SW', startAngle: Math.PI / 2, endAngle: Math.PI },
    { name: 'NW', startAngle: Math.PI, endAngle: 3 * Math.PI / 2 },
  ];
  const cityList = [...cityGroups.keys()].sort();
  const cityToQuad = new Map();
  cityList.forEach((city) => {
    cityToQuad.set(city, cityHash(city) % 4);
  });
  // Cidades por quadrante
  const quadCities = quadrants.map(() => []);
  cityList.forEach((city) => {
    quadCities[cityToQuad.get(city)].push(city);
  });

  // Coloca cada cidade como uma ESTRELA local dentro do seu quadrante.
  //   - Centro da estrela: hub principal (router/switch com maior degree)
  //   - Folhas em volta: outros nodes da cidade orbitando o hub
  // Distribui cidades dentro do quadrante numa grade compacta.
  // Tuning configurável via painel; defaults moderados
  const t = tuning || {};
  const STAR_RING_RADIUS = (t.star_ring ?? 400) * scale;
  const STAR_SPACING = (t.city_spacing ?? 1300) * scale;
  const QUAD_OFFSET = (t.quad_offset ?? 1800) * scale;

  quadrants.forEach((q, qi) => {
    const cities = quadCities[qi];
    if (cities.length === 0) return;
    const cols = Math.ceil(Math.sqrt(cities.length));
    cities.forEach((city, ci) => {
      const col = ci % cols;
      const row = Math.floor(ci / cols);
      const localX = (col + 0.5) * STAR_SPACING;
      const localY = (row + 0.5) * STAR_SPACING;
      // Posição base do centro da estrela depende do quadrante
      let cx = 0; let cy = 0;
      if (q.name === 'NE') { cx =  QUAD_OFFSET + localX; cy = -QUAD_OFFSET - localY; }
      if (q.name === 'SE') { cx =  QUAD_OFFSET + localX; cy =  QUAD_OFFSET + localY; }
      if (q.name === 'SW') { cx = -QUAD_OFFSET - localX; cy =  QUAD_OFFSET + localY; }
      if (q.name === 'NW') { cx = -QUAD_OFFSET - localX; cy = -QUAD_OFFSET - localY; }

      const cityNodes = cityGroups.get(city);
      // Hub = node com maior degree (provavelmente o switch core da cidade)
      const sorted = cityNodes.slice().sort((a, b) => b.degree() - a.degree());
      const hub = sorted[0];
      const leaves = sorted.slice(1);

      // Hub no centro da estrela
      constraints.push({ nodeId: hub.id(), position: { x: cx, y: cy } });

      // Folhas em volta — círculo completo
      const n = leaves.length;
      if (n > 0) {
        const r = STAR_RING_RADIUS * Math.min(1.5, 0.5 + n / 8);
        leaves.forEach((leaf, li) => {
          const angle = (li / n) * 2 * Math.PI;
          constraints.push({
            nodeId: leaf.id(),
            position: { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) },
          });
        });
      }
    });
  });

  // Isolados: coluna lateral à esquerda, fora do mapa principal
  if (isolated.length > 0) {
    const maxQuadCities = Math.max(...quadCities.map((c) => c.length), 1);
    const farLeft = -(QUAD_OFFSET + Math.ceil(Math.sqrt(maxQuadCities)) * STAR_SPACING + 1000 * scale);
    const spacing = 100 * scale;
    const totalH = (isolated.length - 1) * spacing;
    isolated.forEach((node, i) => {
      constraints.push({
        nodeId: node.id(),
        position: { x: farLeft, y: (i * spacing) - totalH / 2 },
      });
    });
  }

  return constraints;
}


function buildFcoseLayout(cy, explosionLevel, useRadialHierarchy = true, tuning = null) {
  // explosionLevel: 0-100 → parâmetros de expansão (operador ajusta via painel).
  const t = tuning || {};
  const nodeRepulsion = (t.repulsion_base ?? 15000) + explosionLevel * (t.repulsion_step ?? 1000);
  const idealEdgeLength = (t.edge_base ?? 300) + explosionLevel * (t.edge_step ?? 5);
  const nodeSeparation = (t.sep_base ?? 280) + explosionLevel * (t.sep_step ?? 4);
  const gravity = Math.max(0.005, 0.08 - explosionLevel * 0.0008);

  return {
    name: 'fcose',
    quality: 'default',
    animate: 'end',
    animationDuration: 500,
    randomize: false,
    nodeRepulsion,
    idealEdgeLength,
    edgeElasticity: 0.3,
    gravity,
    gravityRangeCompound: 1.0,
    numIter: 2500,
    tile: true,
    nodeSeparation,
    packComponents: true,
    fixedNodeConstraint: useRadialHierarchy ? computeRadialConstraints(cy, explosionLevel, tuning) : [],
  };
}


// Passo anti-sobreposição pós-layout (aprendido dos mapas reais do Horus): o
// fcose ainda deixa nós encostando; empurra pares mais próximos que minDist até
// ninguém sobrepor, preservando a estrutura (só move quem colide). Grade espacial
// => O(n) por iteração.
function resolveOverlaps(cy, minDist = 70, iters = 60) {
  const nodes = cy.nodes().filter((n) => !n.isParent() && !n.locked());
  if (nodes.length < 2) return;
  const pos = {};
  nodes.forEach((n) => {
    const p = n.position();
    pos[n.id()] = { x: p.x, y: p.y };
  });
  const cell = minDist;
  for (let it = 0; it < iters; it++) {
    const grid = {};
    nodes.forEach((n) => {
      const p = pos[n.id()];
      const key = `${Math.floor(p.x / cell)},${Math.floor(p.y / cell)}`;
      (grid[key] = grid[key] || []).push(n.id());
    });
    let moved = false;
    nodes.forEach((n) => {
      const id = n.id();
      const p = pos[id];
      const gx0 = Math.floor(p.x / cell);
      const gy0 = Math.floor(p.y / cell);
      for (let gx = gx0 - 1; gx <= gx0 + 1; gx++) {
        for (let gy = gy0 - 1; gy <= gy0 + 1; gy++) {
          const bucket = grid[`${gx},${gy}`];
          if (!bucket) continue;
          bucket.forEach((mid) => {
            if (mid <= id) return; // cada par uma vez
            const q = pos[mid];
            const dx = q.x - p.x;
            const dy = q.y - p.y;
            const d = Math.hypot(dx, dy) || 0.01;
            if (d < minDist) {
              const push = (minDist - d) / 2;
              const ux = dx / d;
              const uy = dy / d;
              p.x -= ux * push;
              p.y -= uy * push;
              q.x += ux * push;
              q.y += uy * push;
              moved = true;
            }
          });
        }
      }
    });
    if (!moved) break;
  }
  cy.batch(() => {
    nodes.forEach((n) => n.position(pos[n.id()]));
  });
}

const CyTopologyMap = React.forwardRef(function CyTopologyMap({
  graph,
  alarms,
  traffic,
  activeKinds,
  activeHostFilters,
  showIssues = true,
  viewMode = 'explodido',
  explosionLevel = 40,
  unfoldParallel = false,
  showTraffic = true,
  nodeMetrics = null,
  edgeSessions = null,
  fillSysmapEdges = false,
  implodeLeaves = false,
  hostTypeFilter = null,
  layoutTuning = null,
  onNodeClick,
  onEdgeClick,
  mapBg,
}, ref) {
  const theme = useTheme();
  const cyRef = useRef(null);

  const stylesheet = useMemo(() => makeStylesheet(theme), [theme]);
  const { elements, usePreset } = useMemo(
    () => graphToCyElements(graph, viewMode, unfoldParallel, fillSysmapEdges),
    [graph, viewMode, unfoldParallel, fillSysmapEdges],
  );

  // Layout inicial — mount-only. Depois o useEffect de `graph` dispara o layout hierárquico.
  // Uso `null` como placeholder (Cytoscape ignora e não roda nada no mount, só cria os elements).
  const initialLayoutOptions = useMemo(
    () => (usePreset
      ? { name: 'preset', fit: true, padding: 50, animate: false }
      : { name: 'preset', fit: false, animate: false }),
    // eslint-disable-next-line
    []
  );

  // API pro parent via ref
  React.useImperativeHandle(ref, () => ({
    relayout: (lvl) => {
      if (!cyRef.current) return;
      const cy = cyRef.current;
      // Em zabbix/sysmap mode não temos layout fcose — escalamos as posições
      // originais a partir do centróide. Guardamos origX/origY na primeira
      // chamada pra que níveis subsequentes sempre escalam da baseline.
      if (usePreset) {
        const level = lvl ?? explosionLevel;
        const scale = 0.5 + (level / 50); // 0→0.5x, 40→1.3x, 100→2.5x
        // Garante baseline
        let sumX = 0; let sumY = 0; let n = 0;
        cy.nodes().forEach((node) => {
          if (node.data('origX') == null) {
            node.data('origX', node.position('x'));
            node.data('origY', node.position('y'));
          }
          sumX += node.data('origX');
          sumY += node.data('origY');
          n += 1;
        });
        if (n === 0) return;
        const cx = sumX / n;
        const cy2 = sumY / n;
        cy.nodes().forEach((node) => {
          const ox = node.data('origX');
          const oy = node.data('origY');
          node.position({
            x: cx + (ox - cx) * scale,
            y: cy2 + (oy - cy2) * scale,
          });
        });
        cy.fit(null, 50);
        return;
      }
      // Modo hierárquico: posições vêm do layout radial por cluster (portado do
      // lab, constantes medidas em 201 mapas reais). Não usa fcose — força
      // trata todo nó igual e produz nuvem; aqui a profundidade na árvore vira
      // o raio, então borda/core/distribuição/acesso aparecem em anéis.
      if (viewMode === 'radial' || viewMode === 'grid') {
        const fn = viewMode === 'grid' ? gridLayout : radialLayout;
        const pos = fn(
          cy.nodes().map((n) => ({ data: n.data() })),
          cy.edges().map((e) => ({ data: e.data() })),
          lvl ?? explosionLevel,
        );
        cy.nodes().forEach((n) => {
          const p = pos.get(n.id());
          if (p) n.position({ x: p.x, y: p.y });
        });
        cy.fit(null, 60);
        return;
      }
      const opts = buildFcoseLayout(cy, lvl ?? explosionLevel, true, layoutTuning);
      const _lay = cy.layout(opts);
      _lay.one('layoutstop', () => resolveOverlaps(cy));
      _lay.run();
    },
    fit: () => {
      if (!cyRef.current) return;
      cyRef.current.fit(null, 50);
    },
    centerOnNode: (nodeId) => {
      if (!cyRef.current) return;
      const n = cyRef.current.getElementById(String(nodeId));
      if (n.length) cyRef.current.center(n);
    },
    /**
     * Vai até um equipamento e o deixa evidente: centraliza com zoom, ofusca o
     * resto e mantém os vizinhos legíveis.
     *
     * Diferente de `centerOnNode`, que só move a câmera — num mapa de 479 nós
     * centralizar não ajuda se o nó continua igual aos outros 478. E diferente
     * de `search`, que dá fit no CONJUNTO de resultados: aqui o alvo é um só,
     * então o zoom pode ser fixo em vez de calculado.
     */
    focarNo: (nodeId) => {
      if (!cyRef.current) return false;
      const cy = cyRef.current;
      const n = cy.getElementById(String(nodeId));
      if (!n.length) return false;
      cy.elements().removeClass('focused search-match');
      cy.elements().addClass('faded');
      n.removeClass('faded').addClass('search-match');
      // vizinhos ficam legíveis: achar o equipamento e não ver com quem ele
      // fala resolve metade da pergunta
      n.closedNeighborhood().removeClass('faded');
      cy.animate({ center: { eles: n }, zoom: Math.max(cy.zoom(), 1.1) },
                 { duration: 300 });
      return true;
    },
    limparFoco: () => {
      if (!cyRef.current) return;
      cyRef.current.elements().removeClass('faded focused search-match');
    },
    /**
     * VLAN path mode: recebe Set<number> de VIDs. Dá classe `vlan-lit` pros nodes/edges
     * que carregam pelo menos uma das VLANs selecionadas; ofusca resto.
     * A edge label é rebuildada inline com as VLANs matched em destaque.
     * Set vazio limpa tudo.
     */
    highlightVlanSet: (vidSet) => {
      if (!cyRef.current) return { hosts: 0, edges: 0 };
      const cy = cyRef.current;
      cy.elements().removeClass('vlan-lit vlan-dimmed vlan-edge-lit');

      const vids = vidSet instanceof Set ? vidSet : new Set();
      const currentTraffic = trafficRef.current || {};

      // Rebuild de todas as labels em batch — uma chamada por edge,
      // sem re-entrar (não usa event listeners).
      cy.edges().forEach((e) => {
        const raw = e.data('raw');
        if (!raw) return;
        const vlansOnEdge = e.data('vlans') || [];
        const matched = vids.size > 0
          ? vlansOnEdge.filter((v) => vids.has(Number(v)))
          : [];
        if (matched.length > 0) {
          e.data('matched_vlans', matched);
        } else {
          e.removeData('matched_vlans');
        }
        const t = currentTraffic[raw.id];
        e.data('edgeLabel', buildEdgeLabel(raw, t, matched.length > 0 ? matched : null, modeSetRef.current));

        // Per-endpoint labels: adiciona `:VID,VID` ao lado do nome da iface
        // quando há VLAN matched. Mostra claramente "qual iface carrega qual VLAN".
        const ep = raw.endpoints || {};
        const aIface = ifaceOrFallback(ep.a?.iface, raw, 'a');
        const bIface = ifaceOrFallback(ep.b?.iface, raw, 'b');
        if (matched.length > 0) {
          const vidList = matched.slice(0, 4).join(',') + (matched.length > 4 ? '+' : '');
          e.data('sourceIfaceLabel', `${aIface}:${vidList}`);
          e.data('targetIfaceLabel', `${bIface}:${vidList}`);
        } else {
          e.data('sourceIfaceLabel', aIface);
          e.data('targetIfaceLabel', bIface);
        }
      });

      if (vids.size === 0) {
        return { hosts: 0, edges: 0 };
      }

      const matchedNodes = cy.nodes().filter((n) => {
        const vlans = n.data('vlans') || [];
        return vlans.some((v) => vids.has(Number(v)));
      });
      const matchedEdges = cy.edges().filter((e) => {
        const mv = e.data('matched_vlans') || [];
        return mv.length > 0;
      });

      if (matchedNodes.length === 0 && matchedEdges.length === 0) {
        return { hosts: 0, edges: 0 };
      }
      cy.elements().addClass('vlan-dimmed');
      matchedNodes.removeClass('vlan-dimmed').addClass('vlan-lit');
      matchedEdges.removeClass('vlan-dimmed').addClass('vlan-edge-lit');
      matchedEdges.connectedNodes().removeClass('vlan-dimmed');

      const union = matchedNodes.union(matchedEdges).union(matchedEdges.connectedNodes());
      ajustarSeForaDaTela(cy, union, 120);
      return { hosts: matchedNodes.length, edges: matchedEdges.length };
    },
    // Compat alias pro handleVlanHighlight antigo (single-vlan → Set)
    highlightVlan: (vid) => {
      // deprecated — mantido pra compat. Use highlightVlanSet.
      const cy = cyRef.current;
      if (!cy) return { hosts: 0, edges: 0 };
      cy.elements().removeClass('vlan-lit vlan-dimmed vlan-edge-lit');
      cy.edges().forEach((e) => e.removeData('matched_vlans'));
      return { hosts: 0, edges: 0 };
    },

    /**
     * ViewMode — troca a "lente" do mapa. Cada modo aplica um conjunto de
     * regras de visibilidade e configurações auxiliares:
     *
     *   all      — tudo visível
     *   l1       — só evidência física (p2p/shared_lan/l2_desc/ospf_adj/trunk)
     *              + auto-expand todos os LAG bundles
     *   l2       — só L2 (shared_lan/l2_desc/trunk), esconde roteamento
     *              + destaca ERPS rings
     *   l3       — só routing (bgp/ospf_adj/static_route/p2p) com sub-picker
     *              (l3Protocols: Set<string>)
     *   services — só edges que tem lag/vrf/vsi (MPLS service map)
     *   health   — dim tudo, ilumina só hosts com issues/alarmes
     *
     * Quando troca de modo, sempre LIMPA o estado anterior (remove lag-phantoms,
     * tira classes vlan/path/ring lit, etc) pra não acumular lixo visual.
     */
    setViewMode: (modes, l3Protocols, opts) => {
      if (!cyRef.current) return;
      const cy = cyRef.current;
      const strictHosts = !!(opts && opts.strictHosts);
      // Aceita Set ou string (compat)
      const modeSet = modes instanceof Set
        ? modes
        : new Set(typeof modes === 'string' ? [modes] : (modes || ['all']));

      // Persiste modeSet pro buildEdgeLabel (useEffect de traffic, VLAN path etc)
      modeSetRef.current = modeSet;

      // Rebuild edgeLabel (texto do MEIO da edge) respeitando o mode
      const currentTraffic = trafficRef.current || {};
      cy.edges().forEach((e) => {
        const raw = e.data('raw');
        if (!raw) return;
        const t = currentTraffic[raw.id];
        const matched = e.data('matched_vlans') || null;
        e.data('edgeLabel', buildEdgeLabel(raw, t, matched, modeSet));
      });

      // Cleanup universal do estado anterior — incluindo mpls-overlay pra
      // não ficar preso quando operador desativa MPLS chip ou muda de mode.
      cy.elements('edge.lag-phantom').remove();
      cy.edges().removeClass('lag-expanded l1-hidden view-hidden view-dimmed health-lit mpls-overlay solo-focus');
      cy.nodes().removeClass('view-hidden view-dimmed health-lit');

      // Rebuild source/target labels — prioridade unificada:
      //   1. physical_iface (se SVI foi resolvido pra trunk)
      //   2. iface logica (vlan1073, Vlanif100)
      //   3. VLAN fallback (V1073) — só quando não temos iface
      //   4. AS fallback (AS12345) — BGP sem iface
      //   5. '?' — último recurso
      //
      // Não empilhamos mais iface|VLAN na mesma label — operador preferiu
      // "só a interface, e se tiver mais de uma VLAN não tem o que fazer".
      cy.edges().forEach((e) => {
        const raw = e.data('raw');
        if (!raw) return;
        const ep = raw.endpoints || {};

        const makeLabel = (endpoint, side) => {
          const phy = endpoint?.physical_iface;
          const iface = endpoint?.iface;
          // Endpoint label = SÓ o iface (ou physical_iface). Sem VLAN, sem IP,
          // sem AS suffix — esses sinais vão TODOS pro middle label pra
          // operador não ter info redundante perto do host. Single source of truth.
          if (phy && iface && phy !== iface) return abbreviateIface(phy);
          if (phy) return abbreviateIface(phy);
          if (iface) return abbreviateIface(iface);
          // Sem iface resolvida: fallback AS (BGP) ou '?'
          return ifaceOrFallback(null, raw, side);
        };

        let aLabel = makeLabel(ep.a, 'a');
        let bLabel = makeLabel(ep.b, 'b');

        // Append IP real do endpoint SÓ em "all" mode. Em L3 puro o IP já
        // aparece no middle label (subnet + bgp); em L2/L1 puros poluiria.
        // shared_lan tem exceção pq IP é a identidade do domínio.
        const kindsOnEdge = raw.kinds || [];
        const isSharedLan = kindsOnEdge.includes('shared_lan');
        const showIp = modeSet.has('all') || isSharedLan;
        if (showIp && raw.subnet && ep.a?.ip) aLabel = `${aLabel} ${ep.a.ip}`;
        if (showIp && raw.subnet && ep.b?.ip) bLabel = `${bLabel} ${ep.b.ip}`;

        e.data('sourceIfaceLabel', aLabel);
        e.data('targetIfaceLabel', bLabel);
      });

      // Se "all" mode E todos os L3 chips estão ativos → sem filtro (default).
      // Se "all" mode mas operador desmarcou algum L3 chip → aplica filtro
      // mesmo assim (esconde edges dos protocolos não selecionados).
      const isAllMode = modeSet.has('all') || modeSet.size === 0;
      const allL3Chips = new Set(['bgp', 'ospf_adj', 'mpls', 'static_route']);
      const activeChips = l3Protocols instanceof Set ? l3Protocols : new Set();
      const allChipsActive = allL3Chips.size === activeChips.size
        && [...allL3Chips].every((k) => activeChips.has(k));
      // Modo SOLO — quando só 1 filtro está ativo (1 OSI ou 1 L3 chip),
      // destaca apenas as edges desse filtro e atenua o resto.
      // Hierarquia: bgp > mpls > ospf > L2 > L1.
      const osiCount = (modeSet.has('l1') ? 1 : 0)
        + (modeSet.has('l2') ? 1 : 0)
        + (modeSet.has('l3') ? 1 : 0);
      const isSoloOsi = !isAllMode && osiCount === 1;
      const isSoloChip = isAllMode && activeChips.size === 1;

      const KIND_HIERARCHY = { bgp: 5, mpls: 4, ospf_adj: 3, trunk: 2, p2p: 1, shared_lan: 1, l2_desc: 1, static_route: 0 };
      const dominantKind = (kinds) => kinds.reduce((a, b) =>
        (KIND_HIERARCHY[a] || 0) >= (KIND_HIERARCHY[b] || 0) ? a : b, kinds[0]);

      const applyMplsOverlay = (edge) => {
        const raw = edge.data('raw');
        const srcData = edge.source().data();
        const tgtData = edge.target().data();
        return raw?.endpoints?.a?.vrf || raw?.endpoints?.b?.vrf
          || (srcData.has_mpls === true && tgtData.has_mpls === true);
      };

      // SOLO L3 chip — esconde edges que não tocam aquele protocolo, destaca o resto
      if (isSoloChip) {
        const onlyChip = [...activeChips][0];
        cy.edges().forEach((edge) => {
          const kinds = edge.data('kinds') || [];
          const mplsMatch = onlyChip === 'mpls' && applyMplsOverlay(edge);
          const kindMatch = kinds.includes(onlyChip);
          if (mplsMatch) edge.addClass('mpls-overlay');
          else edge.removeClass('mpls-overlay');
          if (kindMatch || mplsMatch) {
            edge.removeClass('view-hidden view-dimmed').addClass('solo-focus');
          } else {
            edge.removeClass('solo-focus');
            edge.addClass('view-dimmed');
          }
        });
        return;
      }

      // Even when all chips active, MPLS overlay deve ser aplicado quando
      // MPLS chip ativo (destaca transport entre PEs/Ps). Aplica + return.
      if (isAllMode && allChipsActive) {
        if (activeChips.has('mpls')) {
          cy.edges().forEach((edge) => {
            if (applyMplsOverlay(edge)) edge.addClass('mpls-overlay');
            else edge.removeClass('mpls-overlay');
          });
        }
        return;
      }
      if (isAllMode) {
        // All mode + L3 chips parciais → esconde só edges dos kinds desmarcados.
        // Edges com kinds fora do conjunto L3 (p2p, trunk, etc) ficam visíveis.
        // Também aplica mpls-overlay quando MPLS chip ativo + ambos endpoints
        // têm has_mpls — destaca o transport MPLS em fuchsia.
        cy.edges().forEach((edge) => {
          const kinds = edge.data('kinds') || [];
          const raw = edge.data('raw');
          const srcData = edge.source().data();
          const tgtData = edge.target().data();
          const mplsMatch = activeChips.has('mpls') && (
            raw?.endpoints?.a?.vrf || raw?.endpoints?.b?.vrf
            || (srcData.has_mpls === true && tgtData.has_mpls === true)
          );
          const isL3Edge = kinds.some((k) => allL3Chips.has(k));
          if (!isL3Edge) {
            edge.removeClass('view-hidden');
            if (mplsMatch) edge.addClass('mpls-overlay');
            else edge.removeClass('mpls-overlay');
            return;
          }
          const hasActiveKind = kinds.some((k) => activeChips.has(k));
          if (!hasActiveKind && !mplsMatch) {
            edge.addClass('view-hidden');
            edge.removeClass('mpls-overlay');
          } else {
            edge.removeClass('view-hidden');
            if (mplsMatch) edge.addClass('mpls-overlay');
            else edge.removeClass('mpls-overlay');
          }
        });
        return;
      }

      // Lógica antiga de filtro rodava por UM mode. Agora iteramos cada mode
      // selecionado e UNION — se pelo menos 1 mode deixa visível, fica visível.
      // Pra simplicidade, chamamos cada branch abaixo em sequência pra compor.
      // Os branches modificam `view-hidden` de forma aditiva, então pra UNION
      // fazemos: marca todas escondidas, depois cada mode REMOVE view-hidden
      // das coisas q ele quer mostrar.

      cy.edges().addClass('view-hidden');
      // nodes só escondemos explicitamente nos modes que mexem em node visibility

      const mode = null; // sentinela: processamos cada mode nos blocos abaixo

      // L1 processing — só evidência FÍSICA: p2p (/30 entre 2 hosts) e
      // shared_lan (subnet pequeno com 2-3 hosts). Não inclui OSPF (L3) nem
      // trunk (L2 lógico). Inclui l2_desc só porque é heurística sobre
      // descrição que SUGERE conexão física.
      if (modeSet.has('l1')) {
        const L1_KINDS = new Set(['p2p', 'shared_lan', 'l2_desc']);
        cy.edges().forEach((edge) => {
          const kinds = edge.data('kinds') || [];
          if (kinds.some((k) => L1_KINDS.has(k))) edge.removeClass('view-hidden');
        });
      }
      // L2 processing — VLAN/trunk only. Não inclui p2p (que é L3 /30).
      if (modeSet.has('l2')) {
        const L2_KINDS = new Set(['shared_lan', 'l2_desc', 'trunk']);
        cy.edges().forEach((edge) => {
          const kinds = edge.data('kinds') || [];
          if (kinds.some((k) => L2_KINDS.has(k))) edge.removeClass('view-hidden');
        });
      }
      // L3 processing
      if (modeSet.has('l3')) {
        const active = l3Protocols instanceof Set ? l3Protocols : new Set();
        cy.edges().forEach((edge) => {
          const kinds = edge.data('kinds') || [];
          const raw = edge.data('raw');
          // MPLS: se o operador ligou o chip, ilumina TODA edge onde os 2
          // endpoints têm has_mpls=true (o transporte subjacente que carrega
          // LDP/LSPs/VPNs). A gente ainda não cria edges kind=mpls —
          // mostra a evidência visual via p2p/ospf que conecta os PEs/Ps.
          const srcData = edge.source().data();
          const tgtData = edge.target().data();
          const mplsMatch = active.has('mpls') && (
            // VRF explícito no endpoint
            raw?.endpoints?.a?.vrf || raw?.endpoints?.b?.vrf ||
            // OU ambos os nodes são MPLS (PE/P transport)
            (srcData.has_mpls === true && tgtData.has_mpls === true)
          );
          const kindMatch = kinds.some((k) => active.has(k)) || mplsMatch;
          if (kindMatch) {
            edge.removeClass('view-hidden');
            if (mplsMatch) edge.addClass('mpls-overlay');
            else edge.removeClass('mpls-overlay');
          } else {
            edge.removeClass('mpls-overlay');
          }
        });
        if (strictHosts) {
          const participatingIds = new Set();
          cy.nodes().forEach((node) => {
            const d = node.data();
            if (d.is_virtual && active.has('bgp')) {
              participatingIds.add(node.id());
              return;
            }
            const matches =
              (active.has('bgp') && d.has_bgp === true) ||
              (active.has('ospf_adj') && d.has_ospf === true) ||
              (active.has('mpls') && d.has_mpls === true);
            if (matches) participatingIds.add(node.id());
          });
          cy.nodes().forEach((node) => {
            if (!participatingIds.has(node.id())) node.addClass('view-hidden');
          });
          cy.edges().forEach((edge) => {
            const src = edge.data('source');
            const tgt = edge.data('target');
            if (!participatingIds.has(src) || !participatingIds.has(tgt)) {
              edge.addClass('view-hidden');
            }
          });
        }
      }
      // Health processing
      if (modeSet.has('health')) {
        cy.elements().addClass('view-dimmed');
        cy.nodes().forEach((n) => {
          const issues = n.data('issues_count') || 0;
          if (issues > 0) n.removeClass('view-dimmed').addClass('health-lit').removeClass('view-hidden');
        });
        cy.edges().forEach((e) => {
          const raw = e.data('raw');
          const issues = (raw?.issues || []).length;
          if (issues > 0) e.removeClass('view-dimmed').addClass('health-lit').removeClass('view-hidden');
        });
        cy.nodes('.alarm-disaster, .alarm-high, .alarm-average')
          .removeClass('view-dimmed').addClass('health-lit').removeClass('view-hidden');
      }

      // Auto-expand LAG quando L1 ativo
      if (modeSet.has('l1')) {
        cy.edges().forEach((edge) => {
          if (edge.hasClass('view-hidden') || edge.hasClass('lag-phantom')) return;
          const raw = edge.data('raw');
          const lag = raw?.lag;
          if (!lag) return;
          const ma = lag.a?.members || [];
          const mb = lag.b?.members || [];
          const maxN = Math.max(ma.length, mb.length);
          if (maxN === 0) return;
          const src = edge.data('source');
          const dst = edge.data('target');
          const phantoms = [];
          for (let i = 0; i < maxN; i += 1) {
            phantoms.push({
              data: {
                id: `${edge.id()}:member:${i}`,
                source: String(src), target: String(dst),
                kinds: ['p2p'], layers: ['L1'],
                edgeLabel: `${ma[i] || '?'} ↔ ${mb[i] || '?'}`,
                parent_lag_edge: edge.id(),
              },
              classes: ['lag-phantom'],
            });
          }
          cy.add(phantoms);
          edge.addClass('lag-expanded');
        });
      }

      return;
    },

    /**
     * L1 physical filter mode: quando ON, esconde edges BGP/static_route
     * (ficam só p2p/shared_lan/l2_desc/ospf_adj/trunk = evidência física real)
     * E expande automaticamente todos os LAG bundles pra mostrar membros físicos.
     * Quando OFF, volta ao estado anterior.
     */
    setL1OnlyMode: (enabled) => {
      if (!cyRef.current) return;
      const cy = cyRef.current;
      const L1_KINDS = new Set(['p2p', 'shared_lan', 'l2_desc', 'ospf_adj', 'trunk']);

      if (!enabled) {
        // Collapse todos os LAGs expandidos + mostra tudo
        cy.elements('edge.lag-phantom').remove();
        cy.edges().removeClass('lag-expanded l1-hidden');
        return;
      }

      // Esconde edges sem evidência L1
      cy.edges().forEach((edge) => {
        if (edge.hasClass('lag-phantom')) return;
        const kinds = edge.data('kinds') || [];
        const hasL1 = kinds.some((k) => L1_KINDS.has(k));
        if (hasL1) {
          edge.removeClass('l1-hidden');
        } else {
          edge.addClass('l1-hidden');
        }
      });

      // Auto-expande todos os LAG bundles visíveis
      cy.edges().forEach((edge) => {
        if (edge.hasClass('lag-phantom')) return;
        if (edge.hasClass('l1-hidden')) return;
        if (edge.hasClass('lag-expanded')) return;
        const raw = edge.data('raw');
        const lag = raw?.lag;
        if (!lag) return;
        const membersA = lag.a?.members || [];
        const membersB = lag.b?.members || [];
        const maxMembers = Math.max(membersA.length, membersB.length);
        if (maxMembers === 0) return;
        const source = edge.data('source');
        const target = edge.data('target');
        const phantoms = [];
        for (let i = 0; i < maxMembers; i += 1) {
          phantoms.push({
            data: {
              id: `${edge.id()}:member:${i}`,
              source: String(source),
              target: String(target),
              kinds: ['p2p'],
              layers: ['L1'],
              edgeLabel: `${membersA[i] || '?'} ↔ ${membersB[i] || '?'}`,
              parent_lag_edge: edge.id(),
              lag_member_a: membersA[i],
              lag_member_b: membersB[i],
            },
            classes: ['lag-phantom'],
          });
        }
        cy.add(phantoms);
        edge.addClass('lag-expanded');
      });
    },

    /**
     * Expande uma edge LAG bundle em N edges phantom paralelas (uma por member).
     * Cada phantom tem id `{edgeId}:member:{memberName}`, carrega classe `lag-phantom`
     * e label com o nome do membro. Click de novo (via collapseLagEdge) remove.
     *
     * Retorna { phantoms: n, error? }
     */
    expandLagEdge: (edgeId) => {
      if (!cyRef.current) return { phantoms: 0 };
      const cy = cyRef.current;
      const edge = cy.getElementById(String(edgeId));
      if (edge.length === 0) return { phantoms: 0, error: 'edge não encontrada' };
      const raw = edge.data('raw');
      const lag = raw?.lag;
      if (!lag || (!lag.a?.members?.length && !lag.b?.members?.length)) {
        return { phantoms: 0, error: 'edge não é LAG bundle' };
      }
      // Já expandida? Colapsa.
      if (edge.hasClass('lag-expanded')) {
        cy.elements('edge.lag-phantom').forEach((e) => {
          if (e.data('parent_lag_edge') === String(edgeId)) e.remove();
        });
        edge.removeClass('lag-expanded');
        return { phantoms: 0 };
      }

      const source = edge.data('source');
      const target = edge.data('target');
      // Zip members lado A com lado B (assume mesma ordem de conexão física)
      const membersA = lag.a?.members || [];
      const membersB = lag.b?.members || [];
      const maxMembers = Math.max(membersA.length, membersB.length);

      const phantoms = [];
      for (let i = 0; i < maxMembers; i += 1) {
        const ma = membersA[i];
        const mb = membersB[i];
        const phantomId = `${edgeId}:member:${i}`;
        const phantomLabel = `${ma || '?'} ↔ ${mb || '?'}`;
        phantoms.push({
          data: {
            id: phantomId,
            source: String(source),
            target: String(target),
            kinds: ['p2p'],
            layers: ['L1'],
            edgeLabel: phantomLabel,
            parent_lag_edge: String(edgeId),
            lag_member_a: ma,
            lag_member_b: mb,
          },
          classes: ['lag-phantom'],
        });
      }
      cy.add(phantoms);
      edge.addClass('lag-expanded');
      return { phantoms: phantoms.length };
    },

    /**
     * Path trace entre 2 nodes: BFS no grafo (peso = 1 por edge, ou OSPF cost se existir).
     * Filtra edges pelo vidSet se fornecido (só links que carregam essas VLANs).
     * Passa null/null pra limpar.
     * Retorna { hops: [edge_data, ...], nodes: [node_data, ...], error? }
     */
    /**
     * Path trace com 3 modos de peso:
     *   'hops'     — BFS puro (weight=1), menor número de saltos
     *   'ospf'     — OSPF cost quando disponível (default 10 pra links sem cost)
     *   'reliable' — penaliza shared_lan/l2_desc (confidence baixa), prefere p2p/ospf_adj
     *
     * Filtra por VLAN set se fornecido (só edges carregando as VLANs).
     * Retorna { hops, nodes, totalCost, error? }
     */
    tracePath: (sourceId, targetId, vidSet, pathMode = 'hops') => {
      if (!cyRef.current) return { hops: [], nodes: [] };
      const cy = cyRef.current;
      cy.elements().removeClass('path-trace path-dimmed path-endpoint');

      if (!sourceId || !targetId) {
        return { hops: [], nodes: [] };
      }
      if (sourceId === targetId) {
        return { hops: [], nodes: [], error: 'origem e destino são o mesmo host' };
      }
      const src = cy.getElementById(String(sourceId));
      const dst = cy.getElementById(String(targetId));
      if (src.length === 0 || dst.length === 0) {
        return { hops: [], nodes: [], error: 'host não encontrado no mapa' };
      }

      let roots = cy.elements();
      if (vidSet instanceof Set && vidSet.size > 0) {
        const validEdges = cy.edges().filter((e) => {
          const vlans = e.data('vlans') || [];
          return vlans.some((v) => vidSet.has(Number(v)));
        });
        roots = validEdges.connectedNodes().union(validEdges);
        if (!roots.contains(src) || !roots.contains(dst)) {
          cy.elements().addClass('path-dimmed');
          src.removeClass('path-dimmed').addClass('path-endpoint');
          dst.removeClass('path-dimmed').addClass('path-endpoint');
          return { hops: [], nodes: [], error: 'sem caminho carregando as VLANs selecionadas entre os hosts' };
        }
      }

      const CONFIDENCE_PENALTY = { high: 1, medium: 2, low: 5 };

      const weightFn = (edge) => {
        const raw = edge.data('raw') || {};
        const kinds = edge.data('kinds') || [];
        const confidence = edge.data('confidence') || 'high';

        if (pathMode === 'hops') return 1;

        if (pathMode === 'ospf') {
          // Tenta pegar OSPF cost do endpoint (iface ospf_cost). Fallback: 10.
          const ep = raw.endpoints || {};
          const ospfA = ep.a?.ospf_cost;
          const ospfB = ep.b?.ospf_cost;
          if (ospfA != null) return Number(ospfA);
          if (ospfB != null) return Number(ospfB);
          if (kinds.includes('ospf_adj')) return 10;
          if (kinds.includes('bgp')) return 100;
          return 10;
        }

        // 'reliable' — penaliza edges de baixa confiança
        const base = kinds.includes('bgp') ? 50 : 1;
        return base * (CONFIDENCE_PENALTY[confidence] || 1);
      };

      const dj = roots.dijkstra({ root: src, weight: weightFn });
      const dist = dj.distanceTo(dst);
      const pathCol = dj.pathTo(dst);
      if (!pathCol || pathCol.length === 0 || dist === Infinity) {
        cy.elements().addClass('path-dimmed');
        src.removeClass('path-dimmed').addClass('path-endpoint');
        dst.removeClass('path-dimmed').addClass('path-endpoint');
        return { hops: [], nodes: [], error: 'nenhum caminho encontrado' };
      }

      const pathNodes = pathCol.nodes();
      const pathEdges = pathCol.edges();

      cy.elements().addClass('path-dimmed');
      pathCol.removeClass('path-dimmed').addClass('path-trace');
      src.removeClass('path-dimmed').addClass('path-endpoint');
      dst.removeClass('path-dimmed').addClass('path-endpoint');

      cy.fit(pathCol, 120);

      return {
        hops: pathEdges.map((e) => ({
          id: e.id(),
          source: e.data('source'),
          target: e.data('target'),
          kinds: e.data('kinds') || [],
          weight: weightFn(e),
          raw: e.data('raw'),
        })),
        nodes: pathNodes.map((n) => ({
          id: n.id(),
          label: n.data('label'),
          vendor: n.data('vendor'),
        })),
        totalCost: Math.round(dist),
        pathMode,
      };
    },

    /**
     * Ilumina um anel ERPS: destaca hosts membros + edges que os conectam,
     * ofusca o resto. RPL owner ganha classe extra pra estilo diferente.
     * Passa null pra limpar.
     * Retorna { hosts, edges, rplOwner }.
     */
    highlightRing: (ringId) => {
      if (!cyRef.current) return { hosts: 0, edges: 0, rplOwner: null };
      const cy = cyRef.current;
      cy.elements().removeClass('ring-lit ring-dimmed ring-edge-lit ring-rpl-owner');
      if (ringId === null || ringId === undefined) {
        return { hosts: 0, edges: 0, rplOwner: null };
      }
      const target = Number(ringId);
      let rplOwnerId = null;
      const matchedNodes = cy.nodes().filter((n) => {
        const rings = n.data('ring_ids') || [];
        if (!rings.includes(target)) return false;
        // Marca RPL owner explicitamente
        const erpsRings = n.data('erps_rings') || [];
        const ring = erpsRings.find((r) => r.ring_id === target);
        if (ring && ring.role === 'rpl_owner') {
          rplOwnerId = n.id();
        }
        return true;
      });
      if (matchedNodes.length === 0) {
        return { hosts: 0, edges: 0, rplOwner: null };
      }
      // Edges entre dois membros do ring
      const nodeIds = new Set(matchedNodes.map((n) => n.id()));
      const matchedEdges = cy.edges().filter((e) => (
        nodeIds.has(e.source().id()) && nodeIds.has(e.target().id())
      ));
      cy.elements().addClass('ring-dimmed');
      matchedNodes.removeClass('ring-dimmed').addClass('ring-lit');
      matchedEdges.removeClass('ring-dimmed').addClass('ring-edge-lit');
      if (rplOwnerId) {
        cy.getElementById(rplOwnerId).addClass('ring-rpl-owner');
      }
      const union = matchedNodes.union(matchedEdges);
      ajustarSeForaDaTela(cy, union, 120);
      return { hosts: matchedNodes.length, edges: matchedEdges.length, rplOwner: rplOwnerId };
    },

    /**
     * Filtra/destaca nodes pela query de busca.
     * Se query vazia, limpa destaque. Se casa algum, dá fit neles.
     * Retorna o número de matches.
     */
    search: (query) => {
      if (!cyRef.current) return 0;
      const cy = cyRef.current;
      const predicate = makeSearchPredicate(query);
      if (!predicate) {
        cy.elements().removeClass('faded focused search-match');
        return 0;
      }
      const matched = cy.nodes().filter((n) => predicate(n.data()));
      if (matched.length === 0) {
        // Nenhum match: fade everything
        cy.elements().addClass('faded').removeClass('focused search-match');
        return 0;
      }
      cy.elements().addClass('faded').removeClass('focused search-match');
      matched.addClass('search-match').removeClass('faded');
      matched.closedNeighborhood().removeClass('faded');
      cy.fit(matched, 100);
      return matched.length;
    },
  }), [explosionLevel, usePreset]);

  // Quando elements muda (novo grafo ou unfold toggle), roda o layout hierárquico.
  // Watch em graph.generated_at + unfoldParallel — ambos mudam a estrutura do grafo.
  const graphSignature = graph?.generated_at || (graph ? JSON.stringify([graph.nodes?.length, graph.edges?.length]) : null);
  useEffect(() => {
    if (!cyRef.current || usePreset || !graph) return;
    const cy = cyRef.current;
    const timer = setTimeout(() => {
      // Modo hierárquico não usa fcose: as posições vêm do layout radial por
      // cluster, aplicadas direto. Precisa estar AQUI (e não só no relayout
      // via ref), senão trocar de modo não redesenha nada.
      if (viewMode === 'radial' || viewMode === 'grid') {
        const fn = viewMode === 'grid' ? gridLayout : radialLayout;
        const pos = fn(
          cy.nodes().map((n) => ({ data: n.data() })),
          cy.edges().map((e) => ({ data: e.data() })),
          explosionLevel,
        );
        cy.nodes().forEach((n) => {
          const p = pos.get(n.id());
          if (p) n.position({ x: p.x, y: p.y });
        });
        cy.fit(null, 60);
        return;
      }
      const opts = buildFcoseLayout(cy, explosionLevel, true, layoutTuning);
      const _lay = cy.layout(opts);
      _lay.one('layoutstop', () => resolveOverlaps(cy));
      _lay.run();
    }, 30);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [graphSignature, usePreset, viewMode, unfoldParallel, fillSysmapEdges, layoutTuning]);

  // Filtro de edges por activeKinds + showIssues
  useEffect(() => {
    if (!cyRef.current) return;
    const cy = cyRef.current;
    cy.edges().forEach((edge) => {
      const kinds = edge.data('kinds') || [];
      const kindOk = !activeKinds || kinds.some((k) => activeKinds.has(k));
      if (kindOk) edge.removeClass('hidden');
      else edge.addClass('hidden');
    });
    if (!showIssues) cy.edges('.has-issue').removeClass('has-issue');
  }, [activeKinds, showIssues, graph]);

  // Animação de fluxo nas edges iluminadas (line-dash-offset)
  useEffect(() => {
    if (!cyRef.current) return;
    const cy = cyRef.current;
    let offset = 0;
    const tick = () => {
      offset = (offset - 2) % 1000;
      cy.edges('.vlan-edge-lit').style('line-dash-offset', offset);
    };
    const id = setInterval(tick, 80);
    return () => clearInterval(id);
  }, [graph]);

  // Filtro de HOSTS por categoria (BGP role, OSPF role, MPLS, VLANs)
  // Usa os tests declarados em LayerToggles.HOST_FILTER_TESTS.
  // Sem filtro ativo = mostra todos. Com 1+ chips = OR entre eles.
  useEffect(() => {
    if (!cyRef.current) return;
    const cy = cyRef.current;
    const active = activeHostFilters;
    const hasFilter = active && active.size > 0;

    cy.nodes().forEach((node) => {
      if (!hasFilter) {
        node.removeClass('host-filtered-out');
        return;
      }
      const d = node.data();
      let match = false;
      for (const key of active) {
        const fn = HOST_FILTER_TESTS[key];
        if (fn && fn(d)) { match = true; break; }
      }
      if (match) node.removeClass('host-filtered-out');
      else node.addClass('host-filtered-out');
    });

    // Também oculta edges cujos nodes foram filtrados
    cy.edges().forEach((edge) => {
      const src = cy.getElementById(edge.data('source'));
      const tgt = cy.getElementById(edge.data('target'));
      if (src.hasClass('host-filtered-out') || tgt.hasClass('host-filtered-out')) {
        edge.addClass('host-filtered-out');
      } else {
        edge.removeClass('host-filtered-out');
      }
    });
  }, [activeHostFilters, graph]);

  // Labels dinâmicas + width proporcional ao bps.
  // Quando showTraffic=false, passa traffic=null pra buildEdgeLabel (sem bps
  // na label) E limpa as classes has-traffic / trafficWidth pra remover o
  // overlay visual. Útil quando unfold tem muitas linhas e o bps polui.
  useEffect(() => {
    if (!cyRef.current) return;
    const cy = cyRef.current;
    cy.edges().forEach((edge) => {
      const raw = edge.data('raw');
      if (!raw) return;
      const t = showTraffic ? (traffic || {})[raw.id] : null;
      const matched = edge.data('matched_vlans') || null;
      edge.data('edgeLabel', buildEdgeLabel(raw, t, matched, modeSetRef.current));
      if (t && (t.in_bps || t.out_bps)) {
        const total = (t.in_bps || 0) + (t.out_bps || 0);
        const w = Math.min(14, 2 + Math.max(0, Math.log10(Math.max(total, 1)) - 3));
        edge.data('trafficWidth', w);
        edge.addClass('has-traffic');
      } else {
        edge.removeData('trafficWidth');
        edge.removeClass('has-traffic');
      }
    });
  }, [traffic, graph, showTraffic]);

  // Ref pro traffic atual — permite rebuild de label dentro de highlightVlanSet
  // sem virar dependency cíclica. O handler imperativo lê trafficRef.current.
  const trafficRef = React.useRef(traffic);
  useEffect(() => { trafficRef.current = traffic; }, [traffic]);

  // Ref pro modeSet atual (L1/L2/L3/all). Usado por buildEdgeLabel pra decidir
  // o que entra no label do meio da edge. Atualizado por setViewMode.
  const modeSetRef = React.useRef(new Set(['all']));

  // Implodir folhas — esconde access/olt e mostra badge +N no anchor pai.
  // Operador clica num anchor pra explodir só o subtree dele num grid local.
  //
  // Usa `host_type` do backend (Type.description do tb_hermes_type) pra
  // classificar folhas como OLT/radio. Access = resto. BFS via edges reais
  // pra achar anchor pai (role=border/core/dist).
  const expandedAnchorsRef = React.useRef(new Set());
  const leafToAnchorRef = React.useRef(new Map());
  const anchorChildrenRef = React.useRef(new Map());
  const labelBaseReadyRef = React.useRef(false);

  // Reseta mapas quando graph muda (novo rebuild ou empresa)
  useEffect(() => {
    leafToAnchorRef.current = new Map();
    anchorChildrenRef.current = new Map();
    expandedAnchorsRef.current = new Set();
    labelBaseReadyRef.current = false;
  }, [graph?.generated_at]);

  const applyImplodeState = React.useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    // Salva label base UMA vez logo que o grafo tá pronto
    if (!labelBaseReadyRef.current) {
      cy.nodes().forEach((n) => {
        if (n.data('labelBase') == null) n.data('labelBase', n.data('label') || '');
      });
      labelBaseReadyRef.current = true;
    }
    // Reset de labels pro estado base
    cy.nodes().forEach((n) => {
      const base = n.data('labelBase');
      if (base != null) n.data('label', base);
    });
    cy.nodes('.implode-hidden').removeClass('implode-hidden');
    cy.edges('.implode-hidden').removeClass('implode-hidden');

    if (!implodeLeaves) return;

    // Constrói mapa leaf → anchor se ainda não tiver.
    // Folha = host_type é OLT ou RADIO (periferia real da rede, em 1 hop
    // de alguma borda/core). ROUTER/SWITCH/CGNAT/TELEFONIA NÃO é folha
    // mesmo quando tá num acesso — pode ser switch distribuidor importante.
    if (leafToAnchorRef.current.size === 0) {
      const isLeaf = (d) => {
        const ht = (d.host_type || '').toUpperCase().trim();
        return ht === 'OLT' || ht === 'RADIO';
      };
      // Anchor = qualquer host NÃO-folha que esteja conectado a uma folha.
      // Não depende de role (que é heurística minha de BGP/OSPF).
      const isAnchor = (d) => !isLeaf(d) && !d.is_virtual && !d.is_broadcast_domain;
      cy.nodes().forEach((leaf) => {
        if (!isLeaf(leaf.data())) return;
        // Neighbor direto: se tem vizinho q seja anchor, esse é o pai
        const direct = leaf.neighborhood('node').filter((nb) => isAnchor(nb.data()));
        if (direct.length > 0) {
          // Pega o anchor com menor grau de conexões (provavelmente é o dono real)
          const best = direct.toArray().sort(
            (a, b) => a.degree() - b.degree(),
          )[0];
          const found = best.id();
          leafToAnchorRef.current.set(leaf.id(), found);
          if (!anchorChildrenRef.current.has(found)) anchorChildrenRef.current.set(found, []);
          anchorChildrenRef.current.get(found).push(leaf.id());
          return;
        }
        // BFS até 4 hops — pra folhas que estão atrás de outra folha
        const visited = new Set([leaf.id()]);
        let frontier = [leaf];
        let found = null;
        for (let depth = 0; depth < 4 && !found && frontier.length > 0; depth += 1) {
          const next = [];
          for (const n of frontier) {
            const neighbors = n.neighborhood('node');
            for (let i = 0; i < neighbors.length; i += 1) {
              const nb = neighbors[i];
              if (visited.has(nb.id())) continue;
              visited.add(nb.id());
              if (isAnchor(nb.data())) { found = nb.id(); break; }
              next.push(nb);
            }
            if (found) break;
          }
          frontier = next;
        }
        if (found) {
          leafToAnchorRef.current.set(leaf.id(), found);
          if (!anchorChildrenRef.current.has(found)) anchorChildrenRef.current.set(found, []);
          anchorChildrenRef.current.get(found).push(leaf.id());
        }
      });
    }

    const expanded = expandedAnchorsRef.current;
    leafToAnchorRef.current.forEach((anchorId, leafId) => {
      if (!expanded.has(anchorId)) {
        cy.getElementById(leafId).addClass('implode-hidden');
      }
    });
    cy.edges().forEach((e) => {
      if (e.source().hasClass('implode-hidden') || e.target().hasClass('implode-hidden')) {
        e.addClass('implode-hidden');
      }
    });

    anchorChildrenRef.current.forEach((children, anchorId) => {
      if (expanded.has(anchorId)) return;
      const anchor = cy.getElementById(anchorId);
      if (anchor.length === 0) return;
      const base = anchor.data('labelBase') || '';
      // Preserva as 2 primeiras linhas do label base (nome + IP) e adiciona
      // o badge como 3ª linha. Evita perder o IP quando implode.
      const baseLines = base.split('\n').slice(0, 2).join('\n');
      anchor.data('label', `${baseLines}\n+${children.length}`);
    });

    expanded.forEach((anchorId) => {
      const anchor = cy.getElementById(anchorId);
      if (anchor.length === 0) return;
      const ax = anchor.position('x');
      const ay = anchor.position('y');
      const children = (anchorChildrenRef.current.get(anchorId) || []);
      const cols = Math.max(2, Math.ceil(Math.sqrt(children.length)));
      const spacing = 120;
      children.forEach((cid, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        cy.getElementById(cid).position({
          x: ax + 250 + col * spacing,
          y: ay + (row - cols / 2) * spacing,
        });
      });
    });
  }, [implodeLeaves]);

  useEffect(() => {
    applyImplodeState();
  }, [implodeLeaves, graph, applyImplodeState]);

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !implodeLeaves) return;
    const handler = (evt) => {
      const id = evt.target.id();
      if (!anchorChildrenRef.current.has(id)) return;
      if (expandedAnchorsRef.current.has(id)) expandedAnchorsRef.current.delete(id);
      else expandedAnchorsRef.current.add(id);
      applyImplodeState();
    };
    cy.on('tap', 'node', handler);
    return () => cy.off('tap', 'node', handler);
  }, [implodeLeaves, applyImplodeState]);

  useEffect(() => {
    if (!implodeLeaves) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        expandedAnchorsRef.current.clear();
        applyImplodeState();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [implodeLeaves, applyImplodeState]);

  // Striping descontinuado — bundled edges agora viram N linhas sólidas
  // (uma por protocolo) via auto-unfold em graphToCyElements.
  // Mantém o useEffect só pra limpar classes/data antigas se vierem do cache.
  useEffect(() => {
    if (!cyRef.current) return;
    const cy = cyRef.current;
    cy.edges('.striped').removeClass('striped').forEach((e) => {
      e.removeData('gradientColors');
      e.removeData('gradientPositions');
    });
  }, [graph]);

  // Host type filter — prioridade:
  //   1. host_type exato do banco (tb_hermes_type.description): OLT|RADIO|...
  //   2. fallback: role='olt' (heurística do serializer pra VSOL/Fiberhome/etc)
  //   3. fallback: vendor conhecido de OLT (pra cache antigo sem host_type)
  //   4. default: switch_router
  const classifyHostType = React.useCallback((nodeData) => {
    const raw = (nodeData.host_type || '').toUpperCase().trim();
    if (raw === 'OLT') return 'olt';
    if (raw === 'RADIO') return 'radio';
    if (raw) return 'switch_router';  // ROUTER/SWITCH/SG300/CGNAT/TELEFONIA
    // Sem host_type → fallback pra role + vendor
    if (nodeData.role === 'olt') return 'olt';
    const vendor = (nodeData.vendor || '').toUpperCase();
    if (['VSOL', 'FIBERHOME', 'ZTE', 'CIANET', 'NOKIA', 'PARKS', 'FURUKAWA', 'ZYXEL'].includes(vendor)) return 'olt';
    return 'switch_router';
  }, []);

  useEffect(() => {
    if (!cyRef.current) return;
    const cy = cyRef.current;
    cy.nodes().removeClass('host-type-filtered');
    cy.edges().removeClass('host-type-filtered');
    const active = hostTypeFilter instanceof Set ? hostTypeFilter : null;
    if (!active || active.size === 0 || active.size === 3) return; // tudo ou nada = sem filtro
    cy.nodes().forEach((n) => {
      const type = classifyHostType(n.data());
      if (!active.has(type)) n.addClass('host-type-filtered');
    });
    cy.edges().forEach((e) => {
      if (e.source().hasClass('host-type-filtered') || e.target().hasClass('host-type-filtered')) {
        e.addClass('host-type-filtered');
      }
    });
  }, [hostTypeFilter, graph, classifyHostType]);

  // Edge sessions overlay — colore edges BGP/OSPF verde (up) ou vermelho (down).
  useEffect(() => {
    if (!cyRef.current) return;
    const cy = cyRef.current;
    cy.edges().removeClass('session-up session-down');
    if (!edgeSessions) return;
    cy.edges().forEach((edge) => {
      const eid = edge.id();
      // Para unfold children, tenta o parent_edge_id
      const lookupId = edge.data('parent_edge_id') || eid;
      const sess = edgeSessions[lookupId];
      if (!sess) return;
      if (sess.up) {
        edge.addClass('session-up');
      } else {
        edge.addClass('session-down');
      }
    });
  }, [edgeSessions, graph]);

  // Node metrics overlay — badges CPU/MEM/Temp/PPPoE no label do node.
  // Usa sub-label via node data `metricsLabel` concatenado ao label principal.
  useEffect(() => {
    if (!cyRef.current) return;
    const cy = cyRef.current;
    cy.nodes().forEach((node) => {
      const nodeId = node.id();
      const m = nodeMetrics ? nodeMetrics[nodeId] : null;
      const base = node.data('label') || '';
      // Limpa badge anterior (remove tudo depois de \n)
      const cleanLabel = base.split('\n')[0];

      if (!m) {
        node.data('label', cleanLabel);
        node.removeClass('metrics-warn metrics-critical');
        return;
      }
      const parts = [];
      if (m.cpu_pct != null) parts.push(`CPU ${m.cpu_pct}%`);
      if (m.mem_pct != null) parts.push(`MEM ${m.mem_pct}%`);
      if (m.temp_c != null) parts.push(`${m.temp_c}°C`);
      if (m.pppoe_sessions != null) parts.push(`PPPoE ${m.pppoe_sessions.toLocaleString()}`);

      if (parts.length > 0) {
        node.data('label', `${cleanLabel}\n${parts.join(' · ')}`);
      } else {
        node.data('label', cleanLabel);
      }

      // Thresholds visuais
      const cpuCrit = (m.cpu_pct || 0) > 90;
      const memCrit = (m.mem_pct || 0) > 90;
      const cpuWarn = (m.cpu_pct || 0) > 80;
      const memWarn = (m.mem_pct || 0) > 80;
      const tempCrit = (m.temp_c || 0) > 75;
      const tempWarn = (m.temp_c || 0) > 60;

      if (cpuCrit || memCrit || tempCrit) {
        node.addClass('metrics-critical').removeClass('metrics-warn');
      } else if (cpuWarn || memWarn || tempWarn) {
        node.addClass('metrics-warn').removeClass('metrics-critical');
      } else {
        node.removeClass('metrics-warn metrics-critical');
      }
    });
  }, [nodeMetrics, graph]);

  // Alarmes overlay
  useEffect(() => {
    if (!cyRef.current || !alarms) return;
    const cy = cyRef.current;
    cy.nodes().removeClass('alarm-disaster alarm-high alarm-average');
    cy.nodes().forEach((node) => {
      const zbxId = node.data('hostid_zabbix');
      if (!zbxId) return;
      const a = alarms[String(zbxId)];
      if (!a) return;
      const sev = Number(a.severity);
      if (sev === 5) node.addClass('alarm-disaster');
      else if (sev === 4) node.addClass('alarm-high');
      else if (sev === 3) node.addClass('alarm-average');
    });
  }, [alarms, graph]);

  // Click handlers + highlight neighborhood
  const handleNodeTap = useCallback((evt) => {
    const cy = cyRef.current;
    if (!cy) return;
    const node = evt.target;
    const nbh = node.closedNeighborhood();
    // Fade tudo, destaca só a neighborhood
    cy.elements().addClass('faded');
    nbh.removeClass('faded');
    cy.elements().removeClass('focused');
    node.addClass('focused');
    nbh.edges().addClass('focused');
    onNodeClick && onNodeClick(node.data('id'), node.data());
  }, [onNodeClick]);

  const handleBackgroundTap = useCallback((evt) => {
    const cy = cyRef.current;
    if (!cy) return;
    if (evt.target === cy) {
      cy.elements().removeClass('faded focused');
    }
  }, []);

  const handleEdgeTap = useCallback((evt) => {
    const edge = evt.target;
    // Phantoms LAG não abrem dialog — são só visual
    if (edge.hasClass('lag-phantom')) return;
    onEdgeClick && onEdgeClick(edge.data('raw'));
  }, [onEdgeClick]);

  // Double-click em edge LAG: expand/collapse inline
  const handleEdgeDoubleTap = useCallback((evt) => {
    const edge = evt.target;
    if (edge.hasClass('lag-phantom')) return;
    const raw = edge.data('raw');
    const lag = raw?.lag;
    if (!lag || (!lag.a?.members?.length && !lag.b?.members?.length)) return;
    // Usa o método via ref (já dentro do próprio componente — chama direto)
    const cy = cyRef.current;
    if (!cy) return;
    if (edge.hasClass('lag-expanded')) {
      cy.elements('edge.lag-phantom').forEach((e) => {
        if (e.data('parent_lag_edge') === edge.id()) e.remove();
      });
      edge.removeClass('lag-expanded');
      return;
    }
    const source = edge.data('source');
    const target = edge.data('target');
    const membersA = lag.a?.members || [];
    const membersB = lag.b?.members || [];
    const maxMembers = Math.max(membersA.length, membersB.length);
    const phantoms = [];
    for (let i = 0; i < maxMembers; i += 1) {
      const ma = membersA[i];
      const mb = membersB[i];
      phantoms.push({
        data: {
          id: `${edge.id()}:member:${i}`,
          source: String(source),
          target: String(target),
          kinds: ['p2p'],
          layers: ['L1'],
          edgeLabel: `${ma || '?'} ↔ ${mb || '?'}`,
          parent_lag_edge: edge.id(),
          lag_member_a: ma,
          lag_member_b: mb,
        },
        classes: ['lag-phantom'],
      });
    }
    cy.add(phantoms);
    edge.addClass('lag-expanded');
  }, []);

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.on('tap', 'node', handleNodeTap);
    cy.on('tap', 'edge', handleEdgeTap);
    cy.on('dbltap', 'edge', handleEdgeDoubleTap);
    cy.on('tap', handleBackgroundTap);
    return () => {
      cy.off('tap', 'node', handleNodeTap);
      cy.off('tap', 'edge', handleEdgeTap);
      cy.off('dbltap', 'edge', handleEdgeDoubleTap);
      cy.off('tap', handleBackgroundTap);
    };
  }, [handleNodeTap, handleEdgeTap, handleEdgeDoubleTap, handleBackgroundTap]);

  return (
    <CytoscapeComponent
      elements={elements}
      stylesheet={stylesheet}
      layout={initialLayoutOptions}
      cy={(cy) => { cyRef.current = cy; }}
      style={{ width: '100%', height: '100%', background: mapBg || '#0f172a' }}
      minZoom={0.02}
      maxZoom={5}
      wheelSensitivity={2.5}
    />
  );
});

export default CyTopologyMap;
